import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiArrowLeft, FiEdit2, FiUser, FiFileText, FiClock, FiCheckCircle,
  FiXCircle, FiTrendingUp, FiSave, FiAlertCircle, FiInfo,
  FiActivity, FiChevronRight, FiChevronDown, FiUsers,
  FiPlus, FiTrash2, FiPaperclip, FiPhone, FiMail, FiLock, FiShield, FiCalendar} from 'react-icons/fi';
import { api, constants } from '@crm/shared';
import Layout from '../../components/Layout';
import { toast } from 'react-toastify';
import { fmtDate, fmtDateTime } from '../../utils/dateUtils';
import DateInput from '../../components/DateInput';
import authService from '../../services/authService';
import { useAllUsers } from '../../hooks/useAllUsers';
import { canAccessLeadRecord, getReportScopeUserIds, getReportScopeUsers } from '../../utils/roleAccess';

const { CLOSURE_REASONS } = constants;

/* ─── Helpers ─── */
const todayStr    = () => new Date().toISOString().split('T')[0];
const fmtAmt      = (n) => n != null && n !== '' ? '₹' + Number(n).toLocaleString('en-IN') : '—';
const getUser     = () => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } };

// Open authenticated document — backend requires auth token to access media files
const openDocument = async (url) => {
  if (!url) return;
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) { alert('Document could not be loaded.'); return; }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch { window.open(url, '_blank'); }
};

const isAdmin = () => {
  try {
    const u = getUser();
    if (!u) return false;
    if (u.role && typeof u.role === 'object' && u.role.name) {
      const n = u.role.name.toLowerCase().trim();
      return n === 'admin' || n === 'administrator' || n === 'super admin' || n === 'superadmin';
    }
    if (u.role && typeof u.role === 'string') {
      const r = u.role.toLowerCase().trim();
      return r === 'admin' || r === 'administrator' || r === 'super admin' || r === 'superadmin';
    }
    if (u.role_name) {
      const r = u.role_name.toLowerCase().trim();
      return r === 'admin' || r === 'administrator' || r === 'super admin' || r === 'superadmin';
    }
    if (u.is_superuser === true || u.is_staff === true) return true;
    if (u.role === null || u.role === undefined || u.role === 0) return true;
    return false;
  } catch { return false; }
};

const getRole = () => {
  const u = getUser();
  if (!u?.role) return '';
  if (typeof u.role === 'object') return (u.role.name || '').toLowerCase();
  return String(u.role).toLowerCase();
};

const STAGE_DISPLAY_MAP = {
  FOLLOW_UP: 'Follow Up', LOGIN: 'Login', SANCTION: 'Sanction',
  DISBURSEMENT: 'Disbursement', PDD: 'PDD', PDD_PENDING: 'PDD Pending', 
  REVENUE: 'Revenue', COMPLETE: 'Completed', CLOSED: 'Closed',
};

const PIPELINE_STEPS = [
  { key: 'FOLLOW_UP',    label: 'Follow Up'    },
  { key: 'LOGIN',        label: 'Login'        },
  { key: 'SANCTION',     label: 'Sanction'     },
  { key: 'DISBURSEMENT', label: 'Disbursement' },
  { key: 'PDD',          label: 'PDD'          },
  { key: 'PDD_PENDING',  label: 'PDD Pending'  },
  { key: 'REVENUE',      label: 'Revenue'      },
  { key: 'COMPLETE',     label: 'Completed'    },
  { key: 'CLOSED',       label: 'Closed'       },
];
const PIPELINE          = PIPELINE_STEPS.map(s => s.label);
const STAGE_TO_PIPELINE = Object.fromEntries(PIPELINE_STEPS.map((s, i) => [s.key, i]));
const STAGE_DISPLAY     = Object.fromEntries(PIPELINE_STEPS.map(s => [s.key, s.label]));

const C = {
  navy:'#0D3B5E', navyD:'#082B47', navyL:'#EBF4FF', navyM:'#1A5276',
  gold:'#F5A623', goldL:'#FEF3DC',
  green:'#16A34A', greenL:'#DCFCE7', greenB:'#86EFAC',
  red:'#DC2626',  redL:'#FEF2F2',  redB:'#FECACA',
  sky:'#0284C7',  skyL:'#DBEAFE',
  purple:'#7C3AED', purpleL:'#EDE9FE',
  teal:'#0D9488',   tealL:'#CCFBF1',  tealB:'#5EEAD4',
  border:'#E5E7EB', borderL:'#F3F4F6', bg:'#F4F6F9',
  white:'#FFFFFF', t1:'#111827', t2:'#374151', t3:'#6B7280', t4:'#9CA3AF',
};

const inputSt = {
  display:'block', width:'100%', height:40, padding:'0 12px',
  border:`1.5px solid ${C.border}`, borderRadius:9, fontSize:13,
  fontFamily:'inherit', color:C.t1, background:'#FAFBFC',
  outline:'none', boxSizing:'border-box', transition:'all 0.15s',
};
const iF = (e) => { e.target.style.borderColor=C.navy; e.target.style.background='#fff'; e.target.style.boxShadow='0 0 0 3px rgba(13,59,94,0.09)'; };
const iB = (e) => { e.target.style.borderColor=C.border; e.target.style.background='#FAFBFC'; e.target.style.boxShadow='none'; };

const Card = ({ title, icon, accent=C.navy, children, noPad=false, allowOverflow=false, style={} }) => (
  <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, overflow:allowOverflow?'visible':'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)', ...style }}>
    <div style={{ display:'flex', alignItems:'center', gap:9, padding:'12px 18px', borderBottom:`1px solid ${C.borderL}`, background:'#FAFBFC', borderLeft:`3px solid ${accent}` }}>
      {icon && <span style={{ color:accent, display:'flex', opacity:0.75 }}>{icon}</span>}
      <span style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>{title}</span>
    </div>
    <div style={{ padding: noPad ? 0 : 18 }}>{children}</div>
  </div>
);

const LV = ({ label, value, bold, mono, href, highlight }) => (
  <div>
    <div style={{ fontSize:10.5, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:5 }}>{label}</div>
    {href
      ? <a href={href} style={{ fontSize:13.5, fontWeight:600, color:C.navy, textDecoration:'none', fontFamily:mono?'monospace':'inherit' }}>{value||'—'}</a>
      : <div style={{ fontSize:13.5, fontWeight:bold?700:500, color:highlight||(value?C.t1:C.t4), fontFamily:mono?'monospace':'inherit', lineHeight:1.4 }}>{value||'—'}</div>
    }
  </div>
);

const Fl = ({ label, req, hint, children }) => (
  <div>
    <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.t2, marginBottom:6 }}>
      {label}{req && <span style={{ color:C.red, marginLeft:2 }}>*</span>}
    </label>
    {children}
    {hint && <div style={{ fontSize:11, color:C.t4, marginTop:4 }}>{hint}</div>}
  </div>
);

function SearchableSelect({ value, onChange, options, placeholder, disabled, loading, selectedLabel }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const selected = options.find(o => String(o.value) === String(value));
  const display = selected?.label || selectedLabel || '';
  const q = query.trim().toLowerCase();
  const visible = q
    ? options.filter(o => `${o.label} ${o.meta || ''}`.toLowerCase().includes(q))
    : options;

  return (
    <div ref={wrapRef} style={{ position:'relative' }}>
      <input
        type="text"
        disabled={disabled}
        value={open ? query : display}
        placeholder={loading ? 'Loading...' : placeholder}
        onFocus={() => { if (!disabled) { setOpen(true); setQuery(''); } }}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        style={{ ...inputSt, height:40, paddingRight:34, background:disabled ? '#F3F4F6' : '#FAFBFC' }}
        autoComplete="off"
      />
      <FiChevronDown size={14} style={{ position:'absolute', right:11, top:13, color:C.t4, pointerEvents:'none' }}/>
      {open && !disabled && (
        <div style={{ position:'absolute', zIndex:40, left:0, right:0, top:'calc(100% + 4px)', maxHeight:230, overflowY:'auto', background:C.white, border:`1px solid ${C.border}`, borderRadius:9, boxShadow:'0 14px 32px rgba(15,23,42,0.16)' }}>
          {visible.length === 0 ? (
            <div style={{ padding:'12px', fontSize:12.5, color:C.t4 }}>No matching records</div>
          ) : visible.map(o => (
            <button key={o.value} type="button" onMouseDown={e => e.preventDefault()} onClick={() => { onChange(String(o.value)); setOpen(false); setQuery(''); }}
              style={{ width:'100%', textAlign:'left', padding:'10px 12px', border:'none', borderBottom:`1px solid ${C.borderL}`, background:String(o.value) === String(value) ? C.navyL : C.white, color:C.t1, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
              <span style={{ fontWeight:600 }}>{o.label}</span>
              {o.meta && <span style={{ display:'block', color:C.t4, fontSize:11, marginTop:2 }}>{o.meta}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const Sh = ({ h=20, w='100%', r=6 }) => (
  <div style={{ height:h, width:w, borderRadius:r, background:'linear-gradient(90deg,#F1F5F9 25%,#E2E8F0 50%,#F1F5F9 75%)', backgroundSize:'400px 100%', animation:'cfShimmer 1.4s infinite' }}/>
);

const Spinner = () => (
  <div style={{ width:15, height:15, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.35)', borderTopColor:'#fff', animation:'cfSpin 0.7s linear infinite', flexShrink:0 }}/>
);

const SaveBtn = ({ saving, label, color, icon }) => (
  <button type="submit" disabled={saving}
    style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'10px 22px', borderRadius:9, background:saving?'#9CA3AF':color||C.navy, color:'#fff', border:'none', fontWeight:700, fontSize:13.5, cursor:saving?'not-allowed':'pointer', fontFamily:'inherit', boxShadow:`0 2px 8px rgba(0,0,0,0.20)`, transition:'all 0.12s' }}>
    {saving ? <><Spinner/> Saving…</> : <>{icon||<FiSave size={13}/>} {label}</>}
  </button>
);

const extractError = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  const data = err?.response?.data || err;
  const first = Object.keys(data)[0];
  if (first && Array.isArray(data[first])) return `${first}: ${data[first][0]}`;
  return data?.message || data?.detail || err?.message || fallback;
};

export default function LeadDetail() {
  const currentUser = useMemo(() => authService.getUser(), []);
  const { users: allUsers } = useAllUsers();
  const scopedUsers = useMemo(() => getReportScopeUsers(currentUser, allUsers), [currentUser, allUsers]);
  const scopeUserIds = useMemo(() => getReportScopeUserIds(scopedUsers), [scopedUsers]);

  const { id } = useParams();
  const navigate = useNavigate();

  const [lead,      setLead]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(urlTab === 'revenue' ? 'workflow' : 'overview');
  const [auditOpen, setAuditOpen] = useState(false);

  const [institutions,    setInstitutions]    = useState([]);
  const [channels,        setChannels]        = useState([]);
  const [bankers,         setBankers]         = useState([]);
  const [mastersLoading,  setMastersLoading]  = useState(true);

  const [loginRecord,    setLoginRecord]    = useState(null);
  const [sanctionRecord, setSanctionRecord] = useState(null);
  const [disbursements,  setDisbursements]  = useState([]);
  const [pddRecord,      setPddRecord]      = useState(null);
  const [bankerRecord,   setBankerRecord]   = useState(null);
  const [bankerRecordId, setBankerRecordId] = useState(null);
  const [revenueRecord,  setRevenueRecord]  = useState(null);

  const [loginEditMode,   setLoginEditMode]   = useState(false);
  const [sanctionEditMode,setSanctionEditMode]= useState(false);

  const [sanctionFile,    setSanctionFile]    = useState(null);
  const [sanctionFileUrl, setSanctionFileUrl] = useState(null);
  const sanctionFileRef = useRef(null);

  const [followupForm, setFollowupForm] = useState({ next_follow_up_date:'', remarks:'' });
  const [loginForm, setLoginForm] = useState({ institution_id:'', banker_id:'', channel_id:'', login_date:todayStr(), application_number:'' });
  const [sanctionForm, setSanctionForm] = useState({ sanctioned_amount:'', sanction_date:todayStr(), expected_disbursement_date:'', los_number:'', remarks:'' });
  const [disbForm, setDisbForm] = useState({ disbursement_type:'FULL', disbursement_date:todayStr(), amount:'', insurance_amount:'' });
  const [pddForm, setPddForm] = useState({ status:'PENDING', clearance_date:todayStr(), rejection_reason:'', banker_name:'', banker_contact:'', banker_email:'' });
  const [revenueForm, setRevenueForm] = useState({ invoice_required:true, invoice_number:'', invoice_date:todayStr(), payout_to_company:'', revenue_received:'', received_amount:'', date_of_receipt:'', mode_of_receipt:'' });
  const [closeForm, setCloseForm] = useState({ closure_reason:'', closure_remarks:'' });

  const institutionOptions = useMemo(() => institutions.map(i => ({
    value: String(i.id),
    label: i.name || `Institution #${i.id}`,
    meta: [i.code, i.institution_type].filter(Boolean).join(' | '),
  })), [institutions]);

  const channelOptions = useMemo(() => channels.map(c => ({
    value: String(c.id),
    label: c.name || `Channel #${c.id}`,
    meta: [c.code, c.contact_person, c.email].filter(Boolean).join(' | '),
  })), [channels]);

  const bankerOptions = useMemo(() => {
    const instId = String(loginForm.institution_id || '');
    const currentBankerId = String(loginForm.banker_id || '');
    return bankers
      .filter(b => {
        if (!instId) return true;
        const bInst = String(b.institution?.id ?? b.institution ?? '');
        return bInst === instId || (currentBankerId && String(b.id) === currentBankerId);
      })
      .map(b => {
        const instName = b.institution?.name || institutions.find(i => String(i.id) === String(b.institution))?.name || '';
        return {
          value: String(b.id),
          label: b.name || b.banker_name || `Banker #${b.id}`,
          meta: [instName, b.designation, b.contact_number || b.banker_contact, b.email].filter(Boolean).join(' | '),
        };
      });
  }, [bankers, institutions, loginForm.institution_id, loginForm.banker_id]);

  const fetchLead = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.getLeadById(id);
      const data = res?.data;
      if (!data?.id) throw new Error('Lead not found');
      if (!canAccessLeadRecord(data, currentUser, scopeUserIds)) {
        toast.error('You do not have access to this lead');
        navigate('/leads', { replace: true });
        return;
      }
      setLead(data);
      // Always sync followupForm with latest lead data including remarks
      setFollowupForm(f => ({
        ...f,
        next_follow_up_date: data.next_follow_up_date || f.next_follow_up_date,
        remarks: data.remarks ?? '',
      }));
    } catch (err) {
      toast.error(err?.message || 'Failed to load lead');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [currentUser, id, navigate, scopeUserIds]);

  const fetchStageData = useCallback(async (leadData) => {
    // leadData = lead object — contains login_id, sanction_id, pdd_id
    // NEVER pass null — always pass fresh lead object so direct ID endpoints are used
    // If null is passed (shouldn't happen), read from DOM state as fallback
    try {
      const loginId      = leadData?.login_id;
      const sanctionId   = leadData?.sanction_id;
      const disbIds      = Array.isArray(leadData?.disbursement_id) ? leadData.disbursement_id : [];
      const pddId        = leadData?.pdd_id;

      const [loginRes, sanctionRes, pddRes, bankerRes, revenueRes] = await Promise.allSettled([
        loginId    ? api.getLeadLoginById(loginId)       : api.getLeadLoginByLead(id),
        sanctionId ? api.getLeadSanctionById(sanctionId) : api.getLeadSanctionByLead(id),
        pddId      ? api.getLeadPddById(pddId)           : api.getLeadPddByLead(id),
        api.getBankerByLead(id),
        api.getLeadRevenueByLead(id),
      ]);

      // Disbursements — backend now sends array of IDs, fetch each directly
      // Falls back to ?lead= list if no IDs available
      let disbursementResults = [];
      if (disbIds.length > 0) {
        const disbFetches = await Promise.allSettled(disbIds.map(dId => api.getLeadDisbursementById(dId)));
        disbursementResults = disbFetches
          .filter(r => r.status === 'fulfilled' && r.value?.data?.id)
          .map(r => r.value.data);
      } else {
        // Fallback: use list endpoint with client-side filter
        const disbListRes = await api.getLeadDisbursementsByLead(id);
        let disbAll = Array.isArray(disbListRes?.data?.results) ? disbListRes.data.results : Array.isArray(disbListRes?.data) ? disbListRes.data : [];
        try {
          let nextUrl = disbListRes?.data?.next;
          while (nextUrl) {
            const pageRes = await api.getLeadDisbursementsPage(nextUrl);
            const pageData = pageRes?.data;
            disbAll = [...disbAll, ...(Array.isArray(pageData?.results) ? pageData.results : Array.isArray(pageData) ? pageData : [])];
            nextUrl = pageData?.next || null;
          }
        } catch (_) {}
        disbursementResults = disbAll.filter(d => parseInt(d.lead_id ?? d.lead ?? 0) === parseInt(id));
      }

      const currentLeadId = parseInt(id);
      const toArr = (data) => Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : data?.id ? [data] : [];
      const forThisLead = (arr) => arr.filter(r => {
        const rLead = r.lead_id ?? r.lead ?? null;
        if (rLead === null || rLead === undefined) return true;
        return parseInt(rLead) === currentLeadId;
      });

      // ── Login ──
      const loginData = loginRes?.value?.data;
      // {id}/ returns single object; ?lead= returns paginated list
      const loginArr = loginId
        ? (loginData?.id ? [loginData] : [])
        : forThisLead(toArr(loginData));
      if (loginArr.length > 0) {
        const lr = loginArr[0];
        setLoginRecord(lr);
        const instId = String(lr.institution_id ?? lr.institution_detail?.id ?? lr.institution?.id ?? '');
        const chanId = String(lr.channel_id ?? lr.channel_detail?.id ?? lr.channel?.id ?? '');
        const bkrId  = String(lr.banker_id ?? lr.banker_detail?.id ?? '');
        setLoginForm({ institution_id: instId, banker_id: bkrId, channel_id: chanId, login_date: lr.login_date || todayStr(), application_number: lr.application_number || '' });
      } else { setLoginRecord(null); }

      // ── Sanction ──
      const sanctionData = sanctionRes?.value?.data;
      const sanctionArr = sanctionId
        ? (sanctionData?.id ? [sanctionData] : [])
        : forThisLead(toArr(sanctionData));
      if (sanctionArr.length > 0) {
        const sr = sanctionArr[0];
        setSanctionRecord(sr);
        const loginAppNo = loginArr[0]?.application_number || '';
        const existingLosNo = sr.los_number || '';
        setSanctionForm({ sanctioned_amount: sr.sanctioned_amount || '', sanction_date: sr.sanction_date || todayStr(), expected_disbursement_date: sr.expected_disbursement_date || '', los_number: existingLosNo || loginAppNo, remarks: sr.remarks || '' });
        if (sr.approval_letter) setSanctionFileUrl(sr.approval_letter);
      } else { 
        const loginAppNo = loginArr[0]?.application_number || '';
        setSanctionRecord(null); 
        setSanctionFileUrl(null); 
        setSanctionForm({ sanctioned_amount: '', sanction_date: todayStr(), expected_disbursement_date: '', los_number: loginAppNo, remarks: '' });
      }

      // ── Disbursements — already fetched above using direct IDs or fallback list ──
      setDisbursements(disbursementResults);

      // ── PDD — same as disbursement: backend ?lead= filter broken, fetch all pages + strict filter ──
      let pddAllPages = toArr(pddRes?.value?.data);
      if (!pddId) {
        try {
          let nextPddUrl = pddRes?.value?.data?.next;
          while (nextPddUrl) {
            const pageRes  = await api.getLeadPddPage(nextPddUrl);
            const pageData = pageRes?.data;
            pddAllPages = [...pddAllPages, ...toArr(pageData)];
            nextPddUrl  = pageData?.next || null;
          }
        } catch (_) {}
      }
      const pddArr = pddId
        ? (pddRes?.value?.data?.id ? [pddRes.value.data] : [])
        : pddAllPages.filter(r => {
            const rLead = r.lead_id ?? r.lead ?? null;
            if (rLead === null || rLead === undefined) return false;
            return parseInt(rLead) === currentLeadId;
          });

      // ── Banker ──
      const bData = bankerRes?.value?.data;
      const bAll  = toArr(bData);
      const bArr  = bAll.filter(b => b.banker_name || b.banker_contact)
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      let resolvedBankerName = '', resolvedBankerContact = '', resolvedBankerEmail = '';
      if (bArr.length > 0) {
        const br = bArr[0];
        setBankerRecordId(br.id);
        resolvedBankerName    = br.banker_name    || '';
        resolvedBankerContact = br.banker_contact || '';
        resolvedBankerEmail   = br.banker_email   || '';
        setBankerRecord(resolvedBankerName ? { banker_name: resolvedBankerName, banker_contact: resolvedBankerContact, banker_email: resolvedBankerEmail } : null);
      } else { setBankerRecord(null); setBankerRecordId(null); }

      // ── PDD form ──
      if (pddArr.length > 0) {
        const pr = pddArr[0];
        setPddRecord(pr);
        // Get banker from: pddRecord -> loginRecord (loginArr[0]) -> banker master
        const pddBankerName = pr.banker_detail?.name || pr.banker_name || '';
        const pddBankerContact = pr.banker_detail?.contact_number || pr.banker_contact || '';
        const pddBankerEmail = pr.banker_detail?.email || pr.banker_email || '';
        const lr = loginArr.length > 0 ? loginArr[0] : null;
        const loginBankerName = lr?.banker_detail?.name || '';
        const loginBankerContact = lr?.banker_detail?.contact_number || '';
        const loginBankerEmail = lr?.banker_detail?.email || '';
        setPddForm({
          status:           pr.status           || 'PENDING',
          clearance_date:   pr.clearance_date   || todayStr(),
          rejection_reason: pr.rejection_reason || '',
          banker_name:      resolvedBankerName    || pddBankerName    || loginBankerName    || '',
          banker_contact:   resolvedBankerContact || pddBankerContact || loginBankerContact || '',
          banker_email:     resolvedBankerEmail   || pddBankerEmail   || loginBankerEmail   || '',
        });
      } else {
        setPddRecord(null);
        // Auto-fill PDD banker from login banker detail
        // Try two sources: (1) banker_detail directly in loginRecord, (2) bankers master list
        if (loginArr.length > 0 && !resolvedBankerName) {
          const lr = loginArr[0];
          // Source 1: banker_detail embedded in login record (has name/contact directly)
          if (lr.banker_detail?.id) {
            const bd = lr.banker_detail;
            const bName    = bd.name    || bd.banker_name    || '';
            const bContact = bd.contact_number || bd.mobile_number || bd.phone || bd.banker_contact || '';
            const bEmail   = bd.email   || bd.banker_email   || '';
            if (bName || bContact) {
              setPddForm(f => ({
                ...f,
                banker_name:    f.banker_name    || bName,
                banker_contact: f.banker_contact || bContact,
                banker_email:   f.banker_email   || bEmail,
              }));
            } else {
              // Source 2: bankers master list — try immediately if loaded, else store for later
              const loginBankerId = String(lr.banker_detail.id);
              const bFromMaster = bankers.find(bk => String(bk.id) === loginBankerId);
              if (bFromMaster) {
                setPddForm(f => ({
                  ...f,
                  banker_name:    f.banker_name    || bFromMaster.name || bFromMaster.banker_name || '',
                  banker_contact: f.banker_contact || bFromMaster.contact_number || bFromMaster.banker_contact || bFromMaster.phone || bFromMaster.mobile_number || '',
                  banker_email:   f.banker_email   || bFromMaster.email || bFromMaster.banker_email || '',
                }));
              } else {
                setPddForm(f => ({ ...f, _loginBankerId: loginBankerId }));
              }
            }
          } else if (lr.banker_id) {
            const loginBankerId = String(lr.banker_id);
            const bFromMaster = bankers.find(bk => String(bk.id) === loginBankerId);
            if (bFromMaster) {
              setPddForm(f => ({
                ...f,
                banker_name:    f.banker_name    || bFromMaster.name || bFromMaster.banker_name || '',
                banker_contact: f.banker_contact || bFromMaster.contact_number || bFromMaster.banker_contact || bFromMaster.phone || bFromMaster.mobile_number || '',
                banker_email:   f.banker_email   || bFromMaster.email || bFromMaster.banker_email || '',
              }));
            } else {
              // Bankers not loaded yet — store ID for useEffect to resolve later
              setPddForm(f => ({ ...f, _loginBankerId: loginBankerId }));
            }
          }
        }
      }

      // ── Revenue ──
      const revData = revenueRes?.value?.data;
      const revArr  = forThisLead(toArr(revData));
      if (revArr.length > 0) {
        const rv = revArr[0];
        setRevenueRecord(rv);
        setRevenueForm({ invoice_required: rv.invoice_required !== undefined ? rv.invoice_required : !!(rv.invoice_number), invoice_number: rv.invoice_number || '', invoice_date: rv.invoice_date || todayStr(), payout_to_company: rv.payout_to_company || '', revenue_received: rv.revenue_received || '', received_amount: rv.received_amount || '', date_of_receipt: rv.date_of_receipt || '', mode_of_receipt: rv.mode_of_receipt || '' });
      } else { setRevenueRecord(null); }

    } catch (_) {}
  }, [id]); // eslint-disable-line

  useEffect(() => {
    fetchLead();
    const fetcher = async (apiFn, setFn) => {
      const pages = [];
      let page = 1;
      while (true) {
        const res = await apiFn({ page, size: 500 });
        const data = res?.data?.results || res?.data || [];
        if (!data.length) break;
        pages.push(data);
        if (!res?.data?.next) break;
        page++;
      }
      setFn(pages.flat());
    };
    setMastersLoading(true);
    Promise.all([
      fetcher(api.getInstitutions, setInstitutions),
      fetcher(api.getChannels, setChannels),
      fetcher(api.getBankers, setBankers),
    ]).catch(() => {}).finally(() => setMastersLoading(false));
  }, [fetchLead]);

  useEffect(() => { if (lead?.id) fetchStageData(lead); }, [lead?.id, fetchStageData]); // eslint-disable-line

  useEffect(() => {
    // Auto-fill PDD form banker fields from master when login banker was selected
    // and no existing banker record exists (pddForm has _loginBankerId set)
    setPddForm(f => {
      if (!f._loginBankerId) return f;
      const b = bankers.find(bk => String(bk.id) === f._loginBankerId);
      if (!b) return f;
      return {
        ...f,
        _loginBankerId: undefined,
        banker_name:    f.banker_name    || b.name    || b.banker_name    || '',
        banker_contact: f.banker_contact || b.contact_number || b.banker_contact || b.phone || b.mobile_number || '',
        banker_email:   f.banker_email   || b.email   || b.banker_email   || '',
      };
    });
  }, [bankers, loginRecord]); // eslint-disable-line

  const currentStageRaw = lead?.current_stage || '';
  const isClosed    = currentStageRaw === 'CLOSED';
  const isCompleted = currentStageRaw === 'COMPLETE' || (isClosed && !lead?.closure_stage && !lead?.closure_reason);
  const isCancelled = isClosed && !isCompleted;
  const isTerminated = isClosed || isCompleted;
  const isFollowUp = !isTerminated && (currentStageRaw === 'FOLLOW_UP' || currentStageRaw === '' || (!currentStageRaw && !loginRecord));
  const isConverted = !isTerminated && !isFollowUp;

  const isPddFrozen    = (currentStageRaw === 'PDD' || currentStageRaw === 'PDD_PENDING' || currentStageRaw === 'REVENUE') && !isAdmin();
  const isPddFrozenAll = (currentStageRaw === 'PDD' || currentStageRaw === 'PDD_PENDING' || currentStageRaw === 'REVENUE');

  // ✅ FIX: Record Revenue tab only when PDD is CLEARED
  const isPddCleared = pddRecord?.status === 'CLEARED' || currentStageRaw === 'REVENUE' || currentStageRaw === 'PDD_PENDING';

  const pddClearDate = pddRecord?.clearance_date || lead?.pdd_clearance_date || null;
  const isPastMPlus1 = (() => {
    if (!pddClearDate) return false;
    if (!['PDD','PDD_PENDING','REVENUE'].includes(currentStageRaw)) return false;
    const pdd = new Date(pddClearDate);
    if (isNaN(pdd)) return false;
    return new Date() >= new Date(pdd.getFullYear(), pdd.getMonth() + 2, 1);
  })();

  useEffect(() => {
    if (lead && isPastMPlus1 && !isAdmin()) {
      toast.info('This lead is no longer visible to regular users (M+1 period passed).');
      navigate('/leads', { replace: true });
    }
  }, [isPastMPlus1, lead]); // eslint-disable-line

  const subName = STAGE_DISPLAY[currentStageRaw] || '';
  const currentPipelineIdx = STAGE_TO_PIPELINE[currentStageRaw] ?? (isFollowUp ? 0 : -1);
  const isOverdue      = isFollowUp && lead?.next_follow_up_date && lead.next_follow_up_date < todayStr();
  // Only count disbursements for THIS lead (forThisLead already filters, but double-check)
  const totalDisbursed = disbursements.filter(d => !d.lead || parseInt(d.lead) === parseInt(id)).reduce((s, d) => s + parseFloat(d.amount || 0), 0);

  const setLoginInstitution = (nextInstitutionId) => {
    setLoginForm(f => ({
      ...f,
      institution_id: nextInstitutionId,
      banker_id: String(f.institution_id || '') === String(nextInstitutionId || '') ? f.banker_id : '',
    }));
  };

  const renderLoginMasterFields = () => (
    <>
      <Fl label="Institution">
        <SearchableSelect
          value={loginForm.institution_id}
          onChange={setLoginInstitution}
          options={institutionOptions}
          placeholder="- Select Institution -"
          loading={mastersLoading}
          selectedLabel={loginRecord?.institution_detail?.name}
        />
      </Fl>
      <Fl label="Channel">
        <SearchableSelect
          value={loginForm.channel_id}
          onChange={v => setLoginForm(f => ({ ...f, channel_id:v }))}
          options={channelOptions}
          placeholder="- Select Channel -"
          loading={mastersLoading}
          selectedLabel={loginRecord?.channel_detail?.name}
        />
      </Fl>
      <Fl label="Banker" hint={loginForm.institution_id ? 'Filtered by selected institution' : undefined}>
        <SearchableSelect
          value={loginForm.banker_id}
          onChange={v => setLoginForm(f => ({ ...f, banker_id:v }))}
          options={bankerOptions}
          placeholder="- Select Banker -"
          loading={mastersLoading}
          selectedLabel={loginRecord?.banker_detail?.name}
        />
      </Fl>
    </>
  );

  const handleFollowup = async (e) => {
    e.preventDefault();
    if (!followupForm.next_follow_up_date) { toast.error('Follow-up date required'); return; }
    if (!lead) return;
    setSaving(true);
    try {
      const u = getUser();
      // Backend bug: PATCH validates BSA/internal_staff even for follow-up updates
      // Workaround: include source-related fields to pass backend validation
      const payload = {
        next_follow_up_date: followupForm.next_follow_up_date,
        remarks:             followupForm.remarks.trim() || null,
        updated_by:          u?.id || undefined,
      };
      if (lead.lead_source === 'BSA' && (lead.bsa?.id || lead.bsa_id)) {
        payload.bsa_id = lead.bsa?.id || lead.bsa_id;
      }
      if (lead.lead_source === 'INTERNAL' && (lead.internal_staff?.id || lead.internal_staff_id)) {
        payload.internal_staff_id = lead.internal_staff?.id || lead.internal_staff_id;
      }
      await api.patchLead(id, payload);
      toast.success('Follow-up updated');
      fetchLead();
    } catch (err) { toast.error(extractError(err, 'Failed to update follow-up')); }
    finally { setSaving(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
if (!loginForm.channel_id && !loginForm.banker_id) { toast.error('Select either Channel or Banker'); return; }
    setSaving(true);
    try {
      const u = getUser();
      const payload = {
        lead: parseInt(id),
        login_date: loginForm.login_date,
        application_number: loginForm.application_number.trim() || null,
        institution_id: loginForm.institution_id ? parseInt(loginForm.institution_id) : null,
        channel_id: loginForm.channel_id ? parseInt(loginForm.channel_id) : null,
        banker_id: loginForm.banker_id ? parseInt(loginForm.banker_id) : null,
        created_by: u?.id || null,
        updated_by: u?.id || null,
      };
      if (loginRecord?.id) {
        // PATCH: remove nulls
        const patchPayload = { ...payload };
        Object.keys(patchPayload).forEach(k => { if (patchPayload[k] === null) delete patchPayload[k]; });
        await api.patchLeadLogin(loginRecord.id, patchPayload);
        setLoginEditMode(false);
        toast.success('Login details updated!');
      } else {
        // CREATE: remove nulls
        Object.keys(payload).forEach(k => { if (payload[k] === null) delete payload[k]; });
        await api.createLeadLogin(payload);
        toast.success('Lead converted to Login stage!');
      }
      await new Promise(r => setTimeout(r, 500));
      const freshLead = await api.getLeadById(id); if (freshLead?.data?.id) { setLead(freshLead.data); await fetchStageData(freshLead.data); } else { await fetchLead(true); }
    } catch (err) { toast.error(extractError(err, 'Failed to save login')); }
    finally { setSaving(false); }
  };

  const handleSanction = async (e) => {
    e.preventDefault();
    if (!sanctionForm.sanctioned_amount)          { toast.error('Sanctioned amount required'); return; }
    if (!sanctionForm.expected_disbursement_date) { toast.error('Expected disbursement date required'); return; }
    // Sanctioned amount should not exceed the requested loan amount
    if (lead?.loan_amount && Number(sanctionForm.sanctioned_amount) > Number(lead.loan_amount)) {
      toast.error(`Sanctioned amount (${fmtAmt(sanctionForm.sanctioned_amount)}) cannot exceed loan amount (${fmtAmt(lead.loan_amount)})`);
      return;
    }
    if (sanctionForm.sanction_date && sanctionForm.expected_disbursement_date) {
      const sanc = new Date(sanctionForm.sanction_date); const expd = new Date(sanctionForm.expected_disbursement_date);
      const maxD = new Date(sanc); maxD.setDate(maxD.getDate() + 90);
      if (expd > maxD) { toast.error('Expected disbursement date must be within 3 months of sanction date'); return; }
    }
    setSaving(true);
    try {
      const u = getUser(); let savedRecord;
      if (sanctionFile) {
        const fd = new FormData();
        fd.append('lead', parseInt(id)); fd.append('sanctioned_amount', Number(sanctionForm.sanctioned_amount)); fd.append('sanction_date', sanctionForm.sanction_date || todayStr()); fd.append('expected_disbursement_date', sanctionForm.expected_disbursement_date);
        if (sanctionForm.los_number) fd.append('los_number', sanctionForm.los_number);
        if (sanctionForm.remarks)    fd.append('remarks', sanctionForm.remarks);
        if (u?.id)                   fd.append('updated_by', u.id);
        fd.append('approval_letter', sanctionFile);
        if (sanctionRecord?.id) { savedRecord = await api.patchLeadSanction(sanctionRecord.id, fd); }
        else { if (u?.id) fd.append('created_by', u.id); savedRecord = await api.createLeadSanction(fd); }
        setSanctionFile(null);
      } else {
        const payload = { lead: parseInt(id), sanctioned_amount: Number(sanctionForm.sanctioned_amount), sanction_date: sanctionForm.sanction_date || todayStr(), expected_disbursement_date: sanctionForm.expected_disbursement_date, los_number: sanctionForm.los_number || null, remarks: sanctionForm.remarks || null, created_by: u?.id || null, updated_by: u?.id || null };
        savedRecord = sanctionRecord?.id ? await api.patchLeadSanction(sanctionRecord.id, { ...payload, created_by: undefined }) : await api.createLeadSanction(payload);
      }
      if (savedRecord?.data?.approval_letter) setSanctionFileUrl(savedRecord.data.approval_letter);
      if (savedRecord?.data?.id) setSanctionRecord(savedRecord.data);
      setSanctionEditMode(false);
      toast.success(sanctionRecord?.id ? 'Sanction details updated!' : 'Sanction recorded — lead moved to Sanction stage!');
      await new Promise(r => setTimeout(r, 400)); const freshLead = await api.getLeadById(id); if (freshLead?.data?.id) { setLead(freshLead.data); await fetchStageData(freshLead.data); } else { await fetchLead(true); }
    } catch (err) { toast.error(extractError(err, 'Failed to save sanction')); }
    finally { setSaving(false); }
  };

  const handleDisbursement = async (e) => {
    e.preventDefault();
    if (!disbForm.amount)            { toast.error('Disbursed amount required'); return; }
    if (!disbForm.disbursement_date) { toast.error('Disbursement date required'); return; }
    // SRS FR-5: total disbursement cannot exceed sanctioned amount
    if (sanctionRecord?.sanctioned_amount) {
      const sanctionedAmt    = Number(sanctionRecord.sanctioned_amount);
      const alreadyDisbursed = disbursements.reduce((s, d) => s + parseFloat(d.amount || 0), 0);
      // Block if already at limit
      if (alreadyDisbursed >= sanctionedAmt) {
        toast.error(`Disbursement limit reached. Total already disbursed: ${fmtAmt(alreadyDisbursed)} = Sanctioned: ${fmtAmt(sanctionedAmt)}`);
        return;
      }
      const newTotal = alreadyDisbursed + Number(disbForm.amount);
      if (newTotal > sanctionedAmt) {
        toast.error(`Total disbursement (${fmtAmt(newTotal)}) cannot exceed sanctioned amount (${fmtAmt(sanctionedAmt)}). Remaining: ${fmtAmt(sanctionedAmt - alreadyDisbursed)}`);
        return;
      }
    }
    setSaving(true);
    try {
      const u = getUser();
      const disbRes = await api.createLeadDisbursement({ lead: parseInt(id), disbursement_type: disbForm.disbursement_type, disbursement_date: disbForm.disbursement_date, amount: Number(disbForm.amount), insurance_amount: Number(disbForm.insurance_amount) || 0, created_by: u?.id || null, updated_by: u?.id || null });
      // Directly add to disbursements from response
      if (disbRes?.data?.id) setDisbursements(prev => [...prev.filter(d => parseInt(d.lead||0) === parseInt(id)), disbRes.data]);
      toast.success('Disbursement recorded!');
      setDisbForm({ disbursement_type:'FULL', disbursement_date:todayStr(), amount:'', insurance_amount:'' });
      await new Promise(r => setTimeout(r, 400)); const freshLead = await api.getLeadById(id); if (freshLead?.data?.id) { setLead(freshLead.data); await fetchStageData(freshLead.data); } else { await fetchLead(true); }
    } catch (err) { toast.error(extractError(err, 'Failed to record disbursement')); }
    finally { setSaving(false); }
  };

  const handleDeleteTranche = async (trancheId) => {
    if (['PDD','PDD_PENDING','REVENUE'].includes(lead?.current_stage || '') && !isAdmin()) { toast.error('Only Admin can delete tranches for PDD/Revenue stage leads.'); return; }
    setSaving(true);
    try { await api.deleteLeadDisbursement(trancheId); toast.success('Tranche removed'); const freshLead = await api.getLeadById(id); if (freshLead?.data?.id) { setLead(freshLead.data); fetchStageData(freshLead.data); } else { fetchStageData(lead); } }
    catch { toast.error('Failed to remove tranche'); }
    finally { setSaving(false); }
  };

  const handlePdd = async (e) => {
    e.preventDefault();
    if (!pddForm.clearance_date)        { toast.error('Clearance date required'); return; }
    if (!pddForm.banker_name.trim())    { toast.error('Banker name required'); return; }
    if (!pddForm.banker_contact.trim()) { toast.error('Banker contact required'); return; }
    // Non-admin users submit PDD as CLEARED → moves to PDD_PENDING for Admin approval
    // Admin submits as CLEARED → also moves to PDD_PENDING (then can approve in PDD Management page)
    if (!isAdmin()) {
      setPddForm(f => ({ ...f, status: 'CLEARED', is_cleared: true }));
    }
    setSaving(true);
    try {
      const u = getUser();
      // User submits PDD → status=PENDING (waiting for admin approval)
      // Admin can set status=PENDING or CLEARED directly
      const effectiveStatus = pddForm.status || 'PENDING';
      const pddPayload = { lead: parseInt(id), is_cleared: effectiveStatus === 'CLEARED', clearance_date: pddForm.clearance_date, status: effectiveStatus, rejection_reason: effectiveStatus === 'REJECTED' ? pddForm.rejection_reason : null, created_by: u?.id || null, updated_by: u?.id || null };
      if (pddRecord?.id) { const pr = await api.patchLeadPdd(pddRecord.id, { ...pddPayload, created_by: undefined }); if (pr?.data?.id) setPddRecord(pr.data); }
      else { const pr = await api.createLeadPdd(pddPayload); if (pr?.data?.id) setPddRecord(pr.data); }
      // Save banker details — silently fail if permission denied (backend RBAC issue)
      // Backend developer must grant banker POST/PATCH permission to sales role
      try {
        const bankerPayload = { lead: parseInt(id), banker_name: pddForm.banker_name.trim(), banker_contact: pddForm.banker_contact.trim(), banker_email: pddForm.banker_email.trim() || null, updated_by: u?.id || null };
        if (bankerRecordId) { await api.patchBanker(bankerRecordId, bankerPayload); }
        else { const nb = await api.createBanker({ ...bankerPayload, created_by: u?.id || null }); if (nb?.data?.id) setBankerRecordId(nb.data.id); }
        setBankerRecord({ banker_name: pddForm.banker_name, banker_contact: pddForm.banker_contact, banker_email: pddForm.banker_email });
      } catch (bankerErr) {
        // Banker save failed (likely permission issue) — show warning but don't block PDD save
        console.warn('Banker save failed:', bankerErr);
        // Still update UI with entered values so user can see them
        setBankerRecord({ banker_name: pddForm.banker_name, banker_contact: pddForm.banker_contact, banker_email: pddForm.banker_email });
      }
      if (isAdmin()) {
        toast.success('PDD marked — awaiting Admin approval from PDD Management page');
      } else {
        toast.success('PDD submitted — awaiting Admin approval');
      }
      await new Promise(r => setTimeout(r, 400)); const freshLead = await api.getLeadById(id); if (freshLead?.data?.id) { setLead(freshLead.data); await fetchStageData(freshLead.data); } else { await fetchLead(true); }
    } catch (err) { toast.error(extractError(err, 'Failed to mark PDD')); }
    finally { setSaving(false); }
  };

  const handleRevenue = async (e) => {
    e.preventDefault();
    if (revenueForm.invoice_required && !revenueForm.invoice_number) { toast.error('Invoice number required'); return; }
    if (revenueForm.invoice_required && !revenueForm.invoice_date)   { toast.error('Invoice date required'); return; }
    if (!revenueForm.payout_to_company) { toast.error('Payout to company required'); return; }
    setSaving(true);
    try {
      const u = getUser();
      const payload = { lead: parseInt(id), invoice_required: revenueForm.invoice_required, invoice_number: revenueForm.invoice_required ? revenueForm.invoice_number.trim() : null, invoice_date: revenueForm.invoice_required ? revenueForm.invoice_date : null, payout_to_company: Number(revenueForm.payout_to_company), revenue_received: Number(revenueForm.revenue_received) || 0, received_amount: revenueForm.received_amount ? Number(revenueForm.received_amount) : null, date_of_receipt: revenueForm.date_of_receipt || null, mode_of_receipt: revenueForm.mode_of_receipt || null, created_by: u?.id || null, updated_by: u?.id || null };
      if (revenueRecord?.id) { const rr = await api.patchLeadRevenue(revenueRecord.id, { ...payload, created_by: undefined }); if (rr?.data?.id) setRevenueRecord(rr.data); toast.success('Revenue details updated!'); }
      else { const rr = await api.createLeadRevenue(payload); if (rr?.data?.id) setRevenueRecord(rr.data); toast.success('Revenue recorded — lead moved to Revenue stage!'); }
      await new Promise(r => setTimeout(r, 400)); const freshLead = await api.getLeadById(id); if (freshLead?.data?.id) { setLead(freshLead.data); await fetchStageData(freshLead.data); } else { await fetchLead(true); }
    } catch (err) { toast.error(extractError(err, 'Failed to save revenue')); }
    finally { setSaving(false); }
  };

  const handleComplete = async () => {
    if (!lead) return;
    if (!window.confirm('Mark this lead as Completed? This cannot be undone.')) return;
    setSaving(true);
    try {
      await api.completeLead(id);
      toast.success('Lead marked as Completed!');
      fetchLead();
    } catch (err) { toast.error(extractError(err, 'Failed to complete lead')); }
    finally { setSaving(false); }
  };

  const handleClose = async (e) => {
    e.preventDefault();
    if (!closeForm.closure_reason) { toast.error('Closure reason required'); return; }
    if (!lead) return;
    setSaving(true);
    try {
      const u = getUser();
      // Backend /close/ endpoint expects full lead payload but WITHOUT current_stage
      // (backend handles the stage transition internally — sending current_stage causes
      //  ValidationError: Invalid transition FOLLOW_UP → CLOSED)
      const payload = {
        customer_name:        lead.customer_name,
        mobile_number:        lead.mobile_number,
        email:                lead.email || null,
        loan_amount:          lead.loan_amount,
        lead_source:          lead.lead_source,
        product_id:           lead.product?.id || lead.product_id,
        status_id:            lead.status?.id  || lead.status_id  || null,
        bsa_id:               lead.bsa?.id     || lead.bsa_id     || null,
        internal_staff_id:    lead.internal_staff?.id || lead.internal_staff_id || null,
        location:             lead.location    || null,
        remarks:              lead.remarks     || null,
        next_follow_up_date:  lead.next_follow_up_date || todayStr(),
        closure_reason:       closeForm.closure_reason,
        closure_remarks:      closeForm.closure_remarks || null,
        updated_by:           u?.id || null,
      };
      // Remove null/undefined fields to keep payload clean
      Object.keys(payload).forEach(k => { if (payload[k] === null || payload[k] === undefined) delete payload[k]; });
      await api.closeLead(id, payload);
      toast.success('Lead closed successfully');
      fetchLead();
    } catch (err) { toast.error(extractError(err, 'Failed to close lead')); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <Layout pageTitle="Lead Detail">
      <style>{`@keyframes cfShimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}`}</style>
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
        <Sh h={88} r={14}/><Sh h={70} r={12}/>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 320px', gap:16 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}><Sh h={200} r={12}/><Sh h={160} r={12}/></div>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}><Sh h={180} r={12}/><Sh h={140} r={12}/></div>
        </div>
      </div>
    </Layout>
  );

  if (!lead) return (
    <Layout pageTitle="Lead Not Found">
      <div style={{ textAlign:'center', padding:'80px 0' }}>
        <p style={{ color:C.t3, fontSize:14, marginBottom:20 }}>Lead not found.</p>
        <button onClick={() => navigate('/leads')} className="btn-navy">← Back to Leads</button>
      </div>
    </Layout>
  );

  return (
    <>
      <style>{`
        @keyframes cfShimmer  { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes cfSlideDown{ from{opacity:0;transform:translateY(-5px)} to{opacity:1;transform:translateY(0)} }
        @keyframes cfSpin     { from{transform:rotate(0)} to{transform:rotate(360deg)} }
      `}</style>
      <Layout pageTitle={`Lead: ${lead.customer_name}`}>
        <div style={{ display:'flex', flexDirection:'column', gap:14, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>

          {isPddFrozen && (
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 20px', background:'#FEF3DC', border:'1.5px solid #F0D798', borderRadius:12, fontSize:13, fontWeight:600, color:'#92400E' }}>
              <FiLock size={15} style={{ flexShrink:0 }}/>
              <span><strong>Lead Frozen:</strong> PDD/Revenue stage leads are frozen for regular users. Only Admin can modify.</span>
            </div>
          )}

          {isPastMPlus1 && !isTerminated && (
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 18px', background:'#EDE9FE', border:'1px solid #C4B5FD', borderRadius:10, fontSize:12.5, color:'#5B21B6', fontWeight:600 }}>
              <FiInfo size={14} style={{ flexShrink:0 }}/> This lead is past M+1 from PDD date and is not visible to regular users.
            </div>
          )}

          <div style={{ borderRadius:14, padding:'20px 28px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:14, background:'linear-gradient(120deg,#0D3B5E,#1A5276)', boxShadow:'0 4px 20px rgba(13,59,94,0.20)', position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:'linear-gradient(90deg,#F5A623,#FBBF4A,transparent)' }}/>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <button onClick={() => navigate('/leads')} style={{ width:38, height:38, borderRadius:10, background:'rgba(255,255,255,0.11)', border:'1px solid rgba(255,255,255,0.22)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, cursor:'pointer', transition:'all 0.12s' }} onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,0.20)';}} onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,0.11)';}}>
                <FiArrowLeft size={17}/>
              </button>
              <div>
                <h2 style={{ margin:0, color:'#fff', fontSize:22, fontWeight:800, letterSpacing:'-0.03em', lineHeight:1.2 }}>{lead.customer_name}</h2>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:6, flexWrap:'wrap' }}>
                  <span style={{ fontFamily:'monospace', fontSize:12.5, fontWeight:600, color:'rgba(255,255,255,0.60)', letterSpacing:'0.04em' }}>{lead.lead_no||lead.lead_code||`#${lead.id}`}</span>
                  <span style={{ width:3, height:3, borderRadius:'50%', background:'rgba(255,255,255,0.30)' }}/>
                  <span style={{ fontSize:12.5, color:'rgba(255,255,255,0.55)' }}>{fmtDate(lead.lead_date||lead.created_at)}</span>
                  {lead.lead_source && <span style={{ background:'rgba(255,255,255,0.14)', color:'rgba(255,255,255,0.85)', padding:'2px 9px', borderRadius:999, fontSize:11.5, fontWeight:600, border:'1px solid rgba(255,255,255,0.18)' }}>{lead.lead_source}</span>}
                </div>
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:9, flexWrap:'wrap' }}>
              {subName && !isTerminated && <span style={{ background:'rgba(255,255,255,0.14)', color:'rgba(255,255,255,0.9)', border:'1px solid rgba(255,255,255,0.22)', padding:'4px 11px', borderRadius:999, fontSize:12, fontWeight:600 }}>{subName}</span>}
              {isOverdue && <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'4px 11px', borderRadius:999, fontSize:12, fontWeight:700, background:C.redL, color:C.red, border:`1px solid ${C.redB}` }}><FiAlertCircle size={11}/> OVERDUE</span>}
              {/* SRS Rules: CLOSED → no edit for anyone | PDD/REVENUE non-admin → frozen | else → edit allowed */}
              {isTerminated ? null : isPddFrozen ? (
                <span style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'8px 18px', borderRadius:9, background:'rgba(255,255,255,0.15)', color:'rgba(255,255,255,0.60)', border:'1px solid rgba(255,255,255,0.22)', fontSize:13 }}><FiLock size={13}/> Frozen</span>
              ) : (
                <button onClick={() => navigate(`/leads/${id}/edit`)} style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'8px 18px', borderRadius:9, background:C.gold, color:'#0D1B2A', border:'none', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 2px 10px rgba(245,166,35,0.45)', transition:'all 0.12s' }} onMouseEnter={e=>{e.currentTarget.style.background='#FBBF4A';e.currentTarget.style.transform='translateY(-1px)';}} onMouseLeave={e=>{e.currentTarget.style.background=C.gold;e.currentTarget.style.transform='';}}>
                  <FiEdit2 size={13}/> Edit Lead
                </button>
              )}
            </div>
          </div>

          <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, boxShadow:'0 2px 6px rgba(13,59,94,0.06)', overflow:'hidden' }}>
<div style={{ padding:'12px 20px', background: isCompleted?C.green:isCancelled?C.red:isConverted?C.green:'#C2410C', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                {isClosed && (isCompleted ? <FiCheckCircle size={15} style={{ color:'#fff' }}/> : <FiXCircle size={15} style={{ color:'#fff' }}/>)}
                {isConverted && <FiCheckCircle size={15} style={{ color:'#fff' }}/>}
                {isFollowUp  && <FiClock size={15} style={{ color:'#fff' }}/>}
                <span style={{ color:'#fff', fontWeight:800, fontSize:13.5 }}>
                  {isCompleted ? 'Completed' : isCancelled ? (lead?.closure_stage ? `Closed at ${STAGE_DISPLAY_MAP[lead.closure_stage] || lead.closure_stage}` : 'Closed (Cancelled)') : isConverted ? `Converted → ${subName || 'Login'}` : isOverdue ? 'Follow Up (Overdue)' : 'Under Follow Up'}
                </span>
              </div>
              {!isTerminated && <span style={{ fontSize:11, color:'rgba(255,255,255,0.75)', fontWeight:600 }}>Step {currentPipelineIdx+1} of {PIPELINE.length}</span>}
            </div>
            {!isTerminated && (
              <div style={{ padding:'16px 24px', overflowX:'auto' }}>
                <div style={{ display:'flex', alignItems:'center', minWidth:650 }}>
                  {PIPELINE.map((stage, i) => {
                    const done = currentPipelineIdx > i; const curr = currentPipelineIdx === i;
                    const col  = done ? C.green : curr ? C.navy : '#D1D5DB';
                    return (
                      <React.Fragment key={stage}>
                        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flexShrink:0, position:'relative' }}>
                          <div style={{ width:32, height:32, borderRadius:'50%', border:`2.5px solid ${col}`, display:'flex', alignItems:'center', justifyContent:'center', background:done?C.green:curr?C.navy:'#F9FAFB', transition:'all 0.25s', boxShadow:curr?`0 0 0 5px rgba(13,59,94,0.10)`:'none' }}>
                            {done && <FiCheckCircle size={13} style={{ color:'#fff' }}/>}
                            {curr && <div style={{ width:9, height:9, borderRadius:'50%', background:'#fff' }}/>}
                            {!done && !curr && <span style={{ fontSize:10, fontWeight:700, color:C.t4 }}>{i+1}</span>}
                          </div>
                          <span style={{ fontSize:10, fontWeight:curr?800:600, marginTop:6, whiteSpace:'nowrap', color:done?C.green:curr?C.navy:C.t4 }}>{stage}</span>
                          {curr && <div style={{ position:'absolute', top:-4, right:-4, width:8, height:8, borderRadius:'50%', background:C.gold, boxShadow:`0 0 0 2px ${C.white}` }}/>}
                        </div>
                        {i < PIPELINE.length - 1 && <div style={{ flex:1, height:3, marginTop:-18, minWidth:16, borderRadius:2, background: currentPipelineIdx > i ? C.green : '#E5E7EB', transition:'background 0.4s' }}/>}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {[
              { id:'overview', label:'Overview', icon:<FiInfo size={13}/>,      color:C.navy,    bg:C.navyL,   activeBg:C.navy,    shadow:'rgba(13,59,94,0.28)'   },
              { id:'workflow', label:'Workflow', icon:<FiTrendingUp size={13}/>, color:'#16A34A', bg:'#DCFCE7', activeBg:'#15803D', shadow:'rgba(21,128,61,0.30)'  },
              { id:'activity', label:'Activity', icon:<FiActivity size={13}/>,  color:'#7C3AED', bg:'#EDE9FE', activeBg:'#6D28D9', shadow:'rgba(109,40,217,0.28)' },
            ].map(t => {
              const isAct = activeTab === t.id;
              return (
                <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 20px', border:'none', borderRadius:10, cursor:'pointer', fontFamily:'inherit', fontSize:13.5, transition:'all 0.15s', background:isAct?t.activeBg:t.bg, color:isAct?'#fff':t.color, fontWeight:isAct?700:600, boxShadow:isAct?`0 3px 10px ${t.shadow}`:'none', transform:isAct?'translateY(-1px)':'none' }} onMouseEnter={e=>{if(!isAct){e.currentTarget.style.transform='translateY(-1px)';e.currentTarget.style.boxShadow=`0 3px 10px ${t.shadow}`;} }} onMouseLeave={e=>{if(!isAct){e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='none';} }}>
                  {t.icon} {t.label}
                </button>
              );
            })}
          </div>

          {activeTab === 'overview' && (
            <div style={{ display:'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 300px', gap:14, animation:'cfSlideDown 0.18s ease both' }}>
              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                <Card title="Customer & Loan Information" icon={<FiUser size={13}/>} accent={C.navy}>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px,1fr))', columnGap:24, rowGap:18 }}>
                    <LV label="Customer Name"  value={lead.customer_name} bold/>
                    <LV label="Mobile"         value={lead.mobile_number} mono href={lead.mobile_number?`tel:${lead.mobile_number}`:undefined}/>
                    <LV label="Email"          value={lead.email} href={lead.email?`mailto:${lead.email}`:undefined}/>
                    <LV label="Location"       value={lead.location}/>
                    <LV label="Product"        value={lead.product?.name}/>
                    <LV label="Loan Amount"    value={fmtAmt(lead.loan_amount)} bold highlight={C.navy}/>
                    <LV label="Lead Date"      value={fmtDate(lead.lead_date||lead.created_at)}/>
                    <LV label="Next Follow-up" value={fmtDate(lead.next_follow_up_date)} highlight={isOverdue?C.red:undefined}/>
                    <LV label="Lead Source"    value={lead.lead_source}/>
                  </div>
                  {lead.remarks && (
                    <div style={{ marginTop:18, padding:'12px 16px', background:C.navyL, borderRadius:10, borderLeft:`3px solid ${C.navy}` }}>
                      <div style={{ fontSize:10.5, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:5 }}>Remarks</div>
                      <div style={{ fontSize:13, color:C.t2, lineHeight:1.7 }}>{lead.remarks}</div>
                    </div>
                  )}
                </Card>

                {loginRecord && (
                  <Card title="Login Details" icon={<FiFileText size={13}/>} accent={C.sky}>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', columnGap:24, rowGap:18 }}>
                      <LV label="Login Date"      value={fmtDate(loginRecord.login_date)}/>
                      <LV label="Application No." value={loginRecord.application_number || '—'} mono/>
                      <LV label="Institution"     value={loginRecord.institution_detail?.name || institutions.find(i=>String(i.id)===String(loginRecord.institution_detail?.id))?.name || '—'}/>
                      <LV label="Channel"         value={loginRecord.channel_detail?.name || channels.find(c=>String(c.id)===String(loginRecord.channel_detail?.id))?.name || '—'}/>
                      <LV label="Banker"          value={loginRecord.banker_detail?.name || bankers.find(b=>String(b.id)===String(loginRecord.banker_detail?.id))?.name || '—'}/>
                    </div>
                  </Card>
                )}

                {sanctionRecord && (
                  <Card title="Sanction Details" icon={<FiCheckCircle size={13}/>} accent={C.green}>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', columnGap:24, rowGap:18 }}>
                      <LV label="Sanctioned Amount"     value={fmtAmt(sanctionRecord.sanctioned_amount)} bold highlight={C.green}/>
                      <LV label="Sanction Date"         value={fmtDate(sanctionRecord.sanction_date)}/>
                      <LV label="Expected Disbursement" value={fmtDate(sanctionRecord.expected_disbursement_date)}/>
                      {sanctionRecord.los_number && <LV label="LOS / Account No." value={sanctionRecord.los_number} mono/>}
                      {sanctionRecord.remarks    && <LV label="Remarks" value={sanctionRecord.remarks}/>}
                      {sanctionFileUrl && (
                        <div>
                          <div style={{ fontSize:10.5, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:5 }}>Approval Letter</div>
                          <button type="button" onClick={() => openDocument(sanctionFileUrl)} style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:13, color:C.navy, fontWeight:600, background:C.navyL, padding:'4px 10px', borderRadius:7, border:`1px solid rgba(13,59,94,0.15)`, cursor:'pointer', fontFamily:'inherit' }}>
                            <FiPaperclip size={12}/> View Document
                          </button>
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {disbursements.length > 0 && (
                  <Card title={`Disbursements (${disbursements.length} tranche${disbursements.length!==1?'s':''})`} icon={<span style={{fontSize:13,fontWeight:700}}>₹</span>} accent={C.green}>
                    <div style={{ overflowX:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                        <thead><tr style={{ background:'#F7F9FB' }}>{['#','Type','Date','Amount','Insurance'].map(h => (<th key={h} style={{ padding:'8px 12px', textAlign:h==='Amount'||h==='Insurance'?'right':'left', fontSize:10.5, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.08em', borderBottom:`1px solid ${C.border}` }}>{h}</th>))}</tr></thead>
                        <tbody>
                          {disbursements.map((d, i) => (
                            <tr key={d.id} style={{ borderBottom:i===disbursements.length-1?'none':`1px solid ${C.borderL}` }}>
                              <td style={{ padding:'10px 12px', color:C.t4, fontSize:12 }}>{i+1}</td>
                              <td style={{ padding:'10px 12px' }}><span style={{ background:['Full','FULL'].includes(d.disbursement_type)?C.greenL:C.goldL, color:['Full','FULL'].includes(d.disbursement_type)?C.green:'#92670C', padding:'2px 9px', borderRadius:20, fontSize:11.5, fontWeight:700 }}>{d.disbursement_type}</span></td>
                              <td style={{ padding:'10px 12px', color:C.t2 }}>{fmtDate(d.disbursement_date)}</td>
                              <td style={{ padding:'10px 12px', textAlign:'right', fontWeight:700, color:C.green }}>{fmtAmt(d.amount)}</td>
                              <td style={{ padding:'10px 12px', textAlign:'right', color:C.t3, fontSize:12.5 }}>{d.insurance_amount ? fmtAmt(d.insurance_amount) : '—'}</td>
                            </tr>
                          ))}
                          <tr style={{ background:'#F7F9FB' }}>
                            <td colSpan={3} style={{ padding:'10px 12px', fontWeight:700, color:C.t2 }}>Total Disbursed</td>
                            <td style={{ padding:'10px 12px', textAlign:'right', fontWeight:800, color:C.navy, fontSize:14 }}>{fmtAmt(totalDisbursed)}</td>
                            <td/>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    {sanctionRecord?.sanctioned_amount && (
                      <div style={{ marginTop:12, padding:'10px 14px', background:C.navyL, borderRadius:9, display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:13 }}>
                        <span style={{ color:C.t2 }}>Sanctioned: <strong style={{ color:C.navy }}>{fmtAmt(sanctionRecord.sanctioned_amount)}</strong></span>
                        <span style={{ color:C.t2 }}>Remaining: <strong style={{ color:totalDisbursed>=Number(sanctionRecord.sanctioned_amount)?C.green:C.red }}>{fmtAmt(Number(sanctionRecord.sanctioned_amount)-totalDisbursed)}</strong></span>
                      </div>
                    )}
                  </Card>
                )}

                {(pddRecord?.id || bankerRecord?.banker_name || loginRecord?.banker_detail?.name) && (
                  <Card title="PDD & Banker Details" icon={<FiCheckCircle size={13}/>} accent={C.purple}>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', columnGap:24, rowGap:18 }}>
                      {pddRecord?.id && (<><LV label="PDD Status"
  value={pddRecord.status === 'PENDING' ? 'Pending Admin Approval' : pddRecord.status}
  bold
  highlight={pddRecord.status==='CLEARED'?C.green:pddRecord.status==='REJECTED'?C.red:C.gold}/><LV label="Clearance Date" value={fmtDate(pddRecord.clearance_date)}/>{pddRecord.rejection_reason && <LV label="Rejection Reason" value={pddRecord.rejection_reason}/>}</>)}
                      {(bankerRecord?.banker_name || loginRecord?.banker_detail?.name) && (
                        <>
                          <LV label="Banker Name" value={bankerRecord?.banker_name || loginRecord?.banker_detail?.name || '—'} bold/>
                          <LV label="Banker Contact" value={bankerRecord?.banker_contact || loginRecord?.banker_detail?.contact_number || '—'} mono href={(bankerRecord?.banker_contact || loginRecord?.banker_detail?.contact_number) ? `tel:${bankerRecord?.banker_contact || loginRecord?.banker_detail?.contact_number}` : undefined}/>
                          {(bankerRecord?.banker_email || loginRecord?.banker_detail?.email) && <LV label="Banker Email" value={bankerRecord?.banker_email || loginRecord?.banker_detail?.email} href={`mailto:${bankerRecord?.banker_email || loginRecord?.banker_detail?.email}`}/>}
                        </>
                      )}
                    </div>
                  </Card>
                )}

                {revenueRecord && (
                  <Card title="Revenue Details" icon={<span style={{fontSize:13,fontWeight:700}}>₹</span>} accent={C.teal}>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', columnGap:24, rowGap:18 }}>
                      {revenueRecord.invoice_required !== false && revenueRecord.invoice_number && <LV label="Invoice Number" value={revenueRecord.invoice_number} mono bold/>}
                      {revenueRecord.invoice_required !== false && revenueRecord.invoice_date   && <LV label="Invoice Date" value={fmtDate(revenueRecord.invoice_date)}/>}
                      {revenueRecord.invoice_required === false && <LV label="Invoice" value="Not Required"/>}
                      <LV label="Payout to Company" value={fmtAmt(revenueRecord.payout_to_company)} bold highlight={C.teal}/>
                      {revenueRecord.revenue_received && <LV label="Revenue Received" value={fmtAmt(revenueRecord.revenue_received)} highlight={C.green}/>}
                    </div>
                  </Card>
                )}

                {lead.bsa && (
                  <Card title="BSA Details" icon={<FiUser size={13}/>} accent={C.purple}>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', columnGap:24, rowGap:18 }}>
                      <LV label="BSA Code" value={lead.bsa.code} mono/><LV label="BSA Name" value={lead.bsa.name} bold/><LV label="Mobile" value={lead.bsa.mobile_number} mono href={lead.bsa.mobile_number?`tel:${lead.bsa.mobile_number}`:undefined}/>
                      {lead.bsa.email             && <LV label="Email"    value={lead.bsa.email} href={`mailto:${lead.bsa.email}`}/>}
                      {lead.bsa.payout_percentage && <LV label="Payout %" value={`${lead.bsa.payout_percentage}%`}/>}
                    </div>
                  </Card>
                )}

                {lead.internal_staff && (
                  <Card title="Internal Staff" icon={<FiUsers size={13}/>} accent={C.green}>
                    <LV label="Staff Member" value={`${lead.internal_staff.first_name||''} ${lead.internal_staff.last_name||''}`.trim()||lead.internal_staff.email} bold/>
                  </Card>
                )}

                {isTerminated && (lead.closure_reason || lead.closure_remarks) && (
                  <Card title="Closure Details" icon={<FiXCircle size={13}/>} accent={C.red}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', columnGap:24, rowGap:18 }}>
                      <LV label="Reason"  value={lead.closure_reason}  bold highlight={C.red}/>
                      <LV label="Remarks" value={lead.closure_remarks}/>
                    </div>
                  </Card>
                )}
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                <Card title="Quick Actions" icon={<FiTrendingUp size={13}/>} accent={C.gold}>
                  <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                    {!isTerminated && !isPddFrozen && (
                      <form onSubmit={handleFollowup} style={{ padding:14, background:C.navyL, borderRadius:10, border:`1px solid rgba(13,59,94,0.12)` }}>
                        <div style={{ fontSize:12, fontWeight:700, color:C.navy, marginBottom:10, textTransform:'uppercase', letterSpacing:'0.06em' }}>Update Follow-up</div>
                        <DateInput min={todayStr()} value={followupForm.next_follow_up_date} onChange={e => setFollowupForm(f=>({...f,next_follow_up_date:e.target.value}))} style={{ ...inputSt, marginBottom:8, paddingRight:32 }} onFocus={iF} onBlur={iB}/>
                        <input type="text" autoComplete="off" placeholder="Remark (optional)" value={followupForm.remarks} onChange={e => setFollowupForm(f=>({...f,remarks:e.target.value}))} style={{ ...inputSt, marginBottom:10 }} onFocus={iF} onBlur={iB}/>
                        <button type="submit" disabled={saving} className="btn-navy" style={{ width:'100%', justifyContent:'center', fontSize:13, padding:'8px 16px' }}>{saving?'Saving…':'Save Follow-up'}</button>
                      </form>
                    )}
                    {isPddFrozen && <div style={{ padding:'12px 14px', background:'#FEF3DC', borderRadius:9, border:'1px solid #F0D798', fontSize:12.5, color:'#92400E', display:'flex', alignItems:'center', gap:7 }}><FiLock size={13} style={{ flexShrink:0 }}/> Lead frozen. Contact Admin for changes.</div>}
                    {isFollowUp && !isPddFrozen && <button onClick={() => setActiveTab('workflow')} className="btn-navy" style={{ width:'100%', justifyContent:'center', background:C.green, fontSize:13 }}><FiTrendingUp size={13}/> Convert → Login</button>}
                    {isConverted && !isTerminated && !isPddFrozen && <button onClick={() => setActiveTab('workflow')} className="btn-navy" style={{ width:'100%', justifyContent:'center', fontSize:13 }}><FiChevronRight size={13}/> Advance Stage</button>}
                    {/* FR-7: Close allowed at FOLLOW_UP, LOGIN, SANCTION stages */}
                    {!isTerminated && ['LOGIN','SANCTION','FOLLOW_UP',''].includes(currentStageRaw) && !isPddFrozen && (
                      <button onClick={() => setActiveTab('workflow')} style={{ width:'100%', padding:'9px 16px', borderRadius:9, border:`1px solid ${C.redB}`, background:'#fff', color:C.red, fontWeight:700, fontSize:13, display:'flex', alignItems:'center', justifyContent:'center', gap:6, cursor:'pointer', fontFamily:'inherit', transition:'all 0.12s' }} onMouseEnter={e=>{e.currentTarget.style.background=C.redL;}} onMouseLeave={e=>{e.currentTarget.style.background='#fff';}}><FiXCircle size={13}/> Close Lead</button>
                    )}
                  </div>
                </Card>

                <Card title="Stage Journey" icon={<FiActivity size={13}/>} accent={C.navy}>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {PIPELINE.map((step, i) => {
                      const done = !isTerminated && currentPipelineIdx > i; const curr = !isTerminated && currentPipelineIdx === i; const closed = isTerminated && step === 'Closed';
                      return (
                        <div key={step} style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 10px', borderRadius:8, background:curr||closed?C.navyL:'transparent', border:(curr||closed)?`1px solid rgba(13,59,94,0.12)`:'1px solid transparent' }}>
                          <div style={{ width:20, height:20, borderRadius:'50%', flexShrink:0, border:`2px solid ${curr?C.navy:done?C.green:closed?C.red:'#E5E7EB'}`, display:'flex', alignItems:'center', justifyContent:'center', background:curr?C.navy:done?'#F0FDF4':closed?C.redL:'#F9FAFB' }}>
                            {done && <FiCheckCircle size={10} style={{ color:C.green }}/>}
                            {curr && <div style={{ width:6, height:6, borderRadius:'50%', background:'#fff' }}/>}
                            {closed && <FiXCircle size={10} style={{ color:C.red }}/>}
                          </div>
                          <span style={{ fontSize:13, fontWeight:curr||closed?700:done?500:400, color:curr?C.navy:done?C.t1:closed?C.red:C.t4 }}>{step}</span>
                          {curr && <span style={{ marginLeft:'auto', fontSize:10.5, fontWeight:700, color:C.navy, background:'rgba(13,59,94,0.10)', padding:'2px 8px', borderRadius:999 }}>Current</span>}
                        </div>
                      );
                    })}
                  </div>
                </Card>

                <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
                  <button onClick={() => setAuditOpen(o=>!o)} style={{ width:'100%', padding:'12px 18px', background:'#FAFBFC', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:9, fontFamily:'inherit', borderBottom:auditOpen?`1px solid ${C.borderL}`:'none' }} onMouseEnter={e=>{e.currentTarget.style.background='#F0F4F8';}} onMouseLeave={e=>{e.currentTarget.style.background='#FAFBFC';}}>
                    <FiInfo size={13} style={{ color:C.navy, opacity:0.65 }}/>
                    <span style={{ fontSize:13.5, fontWeight:700, color:C.t1, flex:1, textAlign:'left' }}>Audit Info</span>
                    <FiChevronDown size={14} style={{ color:C.t4, transition:'transform 0.2s', transform:auditOpen?'rotate(180deg)':'none' }}/>
                  </button>
                  {auditOpen && (
                    <div style={{ padding:'14px 18px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                      {[
                        ['Created By', (() => { const d = lead.created_by_detail; if (d) return `${d.first_name||''} ${d.last_name||''}`.trim() || d.email || `User #${d.id}`; if (lead.created_by) return `User #${lead.created_by}`; return 'Not available'; })()],
                        ['Created At', fmtDateTime(lead.created_at)],
                        ['Updated By', (() => { const d = lead.updated_by_detail; if (d) return `${d.first_name||''} ${d.last_name||''}`.trim() || d.email || `User #${d.id}`; if (lead.updated_by) return `User #${lead.updated_by}`; return 'Not available'; })()],
                        ['Updated At', fmtDateTime(lead.updated_at)],
                      ].map(([lbl,val]) => (
                        <div key={lbl}><div style={{ fontSize:10.5, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:4 }}>{lbl}</div><div style={{ fontSize:12.5, color:C.t2 }}>{val}</div></div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'workflow' && (
            <div style={{ display:'flex', flexDirection:'column', gap:14, animation:'cfSlideDown 0.18s ease both' }}>

              {isPddFrozenAll && (
                <div style={{ padding:'18px 22px', background:isPddFrozen?'#FEF3DC':'#EDE9FE', border:`1.5px solid ${isPddFrozen?'#F0D798':'#C4B5FD'}`, borderRadius:12, display:'flex', alignItems:'flex-start', gap:12 }}>
                  <FiLock size={18} style={{ color:isPddFrozen?'#92400E':'#6D28D9', flexShrink:0, marginTop:2 }}/>
                  <div>
                    <div style={{ fontWeight:800, fontSize:14, color:isPddFrozen?'#92400E':'#5B21B6', marginBottom:4 }}>{isPddFrozen ? 'Lead Frozen — Workflow Disabled' : 'PDD/Revenue Stage — Admin Access Only'}</div>
                    <div style={{ fontSize:13, color:isPddFrozen?'#78350F':'#6D28D9', lineHeight:1.6 }}>{isPddFrozen ? 'Only Admin can modify PDD/Revenue stage leads.' : 'Admin mode — PDD/Revenue leads are frozen for regular users. Review carefully before making changes.'}</div>
                  </div>
                </div>
              )}

              {!isPddFrozen && (
                <>
                  {!isTerminated && (
                    <Card title="Update Follow-up Date" icon={<FiClock size={13}/>} accent={C.gold}>
                      <form onSubmit={handleFollowup}>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                          <Fl label="Next Follow-up Date" req><input type="date" min={todayStr()} value={followupForm.next_follow_up_date} onChange={e => setFollowupForm(f=>({...f,next_follow_up_date:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                          <Fl label="Remarks" hint="Notes about this follow-up"><input type="text" autoComplete="off" placeholder="What happened?" value={followupForm.remarks} onChange={e => setFollowupForm(f=>({...f,remarks:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                        </div>
                        <div style={{ marginTop:16 }}><SaveBtn saving={saving} label="Save Follow-up"/></div>
                      </form>
                    </Card>
                  )}

                  {isFollowUp && (
                    <Card title="Convert to Login" icon={<FiCheckCircle size={13}/>} accent={C.green} allowOverflow>
                      <form onSubmit={handleLogin}>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                          <Fl label="Login Date" req><input type="date" max={todayStr()} value={loginForm.login_date} onChange={e => setLoginForm(f=>({...f,login_date:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                          {renderLoginMasterFields()}
                          <Fl label="App No / LOS No" hint="Optional"><input type="text" placeholder="App / LOS ref number" value={loginForm.application_number} onChange={e => setLoginForm(f=>({...f,application_number:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                        </div>
                        <div style={{ marginTop:20 }}><SaveBtn saving={saving} label="Convert to Login" color={C.green}/></div>
                      </form>
                    </Card>
                  )}

                  {/* B-01 Warning: stage=LOGIN but loginRecord not loaded (backend filter bug) */}
                  {isConverted && !loginRecord && currentStageRaw === 'LOGIN' && (
                    <div style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'13px 16px', background:'#FEF3DC', border:'1.5px solid #F0D798', borderRadius:10, fontSize:13, color:'#92400E' }}>
                      <FiAlertCircle size={15} style={{ flexShrink:0, marginTop:1 }}/>
                      <div>
                        <div style={{ fontWeight:700, marginBottom:3 }}>Login record not loading </div>
                        <div style={{ fontWeight:500, lineHeight:1.6 }}>Please refresh or contact support.</div>
                      </div>
                    </div>
                  )}

                  {isConverted && loginRecord && (
                    <Card title="Login Details" icon={<FiCheckCircle size={13}/>} accent={C.green} allowOverflow>
                      {!loginEditMode ? (
                        <>
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                            <div style={{ padding:'8px 14px', background:C.greenL, borderRadius:9, fontSize:12.5, color:'#166534', fontWeight:600, border:`1px solid ${C.greenB}`, display:'flex', alignItems:'center', gap:8 }}><FiCheckCircle size={14}/> Login recorded</div>
                            {currentStageRaw === 'LOGIN' && (
                              <button type="button" onClick={() => setLoginEditMode(true)} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:8, border:`1px solid ${C.border}`, background:C.white, color:C.t2, fontSize:12.5, fontWeight:600, cursor:'pointer', fontFamily:'inherit', transition:'all 0.12s' }} onMouseEnter={e=>{e.currentTarget.style.background=C.navyL;e.currentTarget.style.borderColor=C.navy;}} onMouseLeave={e=>{e.currentTarget.style.background=C.white;e.currentTarget.style.borderColor=C.border;}}>
                                <FiEdit2 size={12}/> Edit Login
                              </button>
                            )}
                          </div>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
                            <LV label="Login Date" value={fmtDate(loginRecord.login_date)}/><LV label="Application No." value={loginRecord.application_number||'—'} mono/><LV label="Institution" value={loginRecord.institution_detail?.name||institutions.find(i=>String(i.id)===String(loginRecord.institution_id))?.name||'—'}/>
                            <LV label="Channel" value={loginRecord.channel_detail?.name||channels.find(c=>String(c.id)===String(loginRecord.channel_id))?.name||'—'}/>
                            <LV label="Banker" value={loginRecord.banker_detail?.name||bankers.find(b=>String(b.id)===String(loginRecord.banker_id))?.name||'—'}/>
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ padding:'10px 14px', background:'#FEF3DC', borderRadius:9, marginBottom:16, fontSize:12.5, color:'#92400E', fontWeight:600, border:'1px solid #F0D798', display:'flex', alignItems:'center', gap:8 }}>
                            <FiAlertCircle size={14}/> You are editing login details — please review carefully.
                          </div>
<form onSubmit={handleLogin}>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                              <Fl label="Login Date" req><input type="date" max={todayStr()} value={loginForm.login_date} onChange={e => setLoginForm(f=>({...f,login_date:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                          {renderLoginMasterFields()}
                              <Fl label="App No / LOS No" hint="Optional"><input type="text" placeholder="App / LOS ref number" value={loginForm.application_number} onChange={e => setLoginForm(f=>({...f,application_number:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                            </div>
                            <div style={{ display:'flex', gap:10, marginTop:20 }}>
                              <SaveBtn saving={saving} label="Update Login" color={C.green}/>
                              <button type="button" onClick={() => setLoginEditMode(false)} disabled={saving} style={{ padding:'10px 18px', borderRadius:9, border:`1px solid ${C.border}`, background:C.white, color:C.t3, fontWeight:600, fontSize:13.5, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
                            </div>
                          </form>
                        </>
                      )}
                    </Card>
                  )}

                  {isConverted && currentStageRaw === 'LOGIN' && (
                    <Card title="Record Sanction" icon={<FiTrendingUp size={13}/>} accent={C.sky}>
                      <form onSubmit={handleSanction}>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                          <Fl label="Sanctioned Amount (₹)" req><input type="number" placeholder="e.g. 5000000" min="1" value={sanctionForm.sanctioned_amount} onChange={e => setSanctionForm(f=>({...f,sanctioned_amount:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                          <Fl label="Sanction Date"><input type="date" max={todayStr()} value={sanctionForm.sanction_date} onChange={e => setSanctionForm(f=>({...f,sanction_date:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                          <Fl label="Expected Disbursement Date" req hint="Within 3 months of sanction date"><input type="date" min={sanctionForm.sanction_date||todayStr()} max={(() => { const d=new Date(sanctionForm.sanction_date||todayStr()); d.setDate(d.getDate()+90); return d.toISOString().split('T')[0]; })()} value={sanctionForm.expected_disbursement_date} onChange={e => setSanctionForm(f=>({...f,expected_disbursement_date:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                          <Fl label="App No / LOS No" hint="Auto from Login"><input type="text" disabled value={sanctionForm.los_number} style={{ ...inputSt, height:40, background:'#F3F4F6', color:'#374151' }} /></Fl>
                          <div style={{ gridColumn:'span 2' }}><Fl label="Remarks" hint="Optional"><textarea rows={2} autoComplete="off" placeholder="Notes..." value={sanctionForm.remarks} onChange={e => setSanctionForm(f=>({...f,remarks:e.target.value}))} style={{ ...inputSt, height:'auto', paddingTop:10, paddingBottom:10, resize:'vertical', lineHeight:1.6 }} onFocus={iF} onBlur={iB}/></Fl></div>
                          <div style={{ gridColumn:'span 2' }}>
                            <Fl label="Approval Letter" hint="Upload bank sanction letter (PDF/image)">
                              <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                                <input type="file" ref={sanctionFileRef} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => setSanctionFile(e.target.files[0]||null)} style={{ display:'none' }}/>
                                <button type="button" onClick={() => sanctionFileRef.current?.click()} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:8, border:`1.5px solid ${sanctionFile?C.navy:C.border}`, background:sanctionFile?C.navyL:'#FAFBFC', color:sanctionFile?C.navy:C.t3, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                                  <FiPaperclip size={13}/> {sanctionFile ? 'Change File' : 'Upload Approval Letter'}
                                </button>
                                {sanctionFile && (<span style={{ fontSize:12.5, color:C.navy, fontWeight:600, display:'flex', alignItems:'center', gap:5 }}><FiCheckCircle size={12} style={{ color:C.green }}/> {sanctionFile.name}<button type="button" onClick={() => { setSanctionFile(null); if(sanctionFileRef.current) sanctionFileRef.current.value=''; }} style={{ background:'none', border:'none', cursor:'pointer', color:C.red, fontSize:11, padding:'0 4px' }}>✕</button></span>)}
                                {!sanctionFile && sanctionFileUrl && (<button type="button" onClick={() => openDocument(sanctionFileUrl)} style={{ fontSize:12.5, color:C.sky, fontWeight:600, display:'inline-flex', alignItems:'center', gap:5, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', padding:0 }}><FiPaperclip size={11}/> Existing document</button>)}
                              </div>
                            </Fl>
                          </div>
                        </div>
                        <div style={{ marginTop:16 }}><SaveBtn saving={saving} label="Save Sanction" color={C.sky}/></div>
                      </form>
                    </Card>
                  )}

                  {isConverted && !['LOGIN'].includes(currentStageRaw) && sanctionRecord && (
                    <Card title="Sanction Details" icon={<FiTrendingUp size={13}/>} accent={C.sky}>
                      {!sanctionEditMode ? (
                        <>
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                            <div style={{ padding:'8px 14px', background:C.skyL, borderRadius:9, fontSize:12.5, color:'#0369A1', fontWeight:600, border:'1px solid #BAE6FD', display:'flex', alignItems:'center', gap:8 }}><FiCheckCircle size={14}/> Sanction recorded</div>
                            {['SANCTION'].includes(currentStageRaw) && (
                              <button type="button" onClick={() => setSanctionEditMode(true)} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:8, border:`1px solid ${C.border}`, background:C.white, color:C.t2, fontSize:12.5, fontWeight:600, cursor:'pointer', fontFamily:'inherit', transition:'all 0.12s' }} onMouseEnter={e=>{e.currentTarget.style.background=C.navyL;e.currentTarget.style.borderColor=C.navy;}} onMouseLeave={e=>{e.currentTarget.style.background=C.white;e.currentTarget.style.borderColor=C.border;}}>
                                <FiEdit2 size={12}/> Edit Sanction
                              </button>
                            )}
                          </div>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
                            <LV label="Sanctioned Amount"     value={fmtAmt(sanctionRecord.sanctioned_amount)} bold highlight={C.sky}/>
                            <LV label="Sanction Date"         value={fmtDate(sanctionRecord.sanction_date)}/>
                            <LV label="Expected Disbursement" value={fmtDate(sanctionRecord.expected_disbursement_date)}/>
                            {sanctionRecord.los_number && <LV label="LOS / Account No." value={sanctionRecord.los_number} mono/>}
                            {sanctionRecord.remarks    && <LV label="Remarks"            value={sanctionRecord.remarks}/>}
                            {sanctionFileUrl && (<div><div style={{ fontSize:10.5, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:5 }}>Approval Letter</div><button type="button" onClick={() => openDocument(sanctionFileUrl)} style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:13, color:C.navy, fontWeight:600, background:C.navyL, padding:'4px 10px', borderRadius:7, border:`1px solid rgba(13,59,94,0.15)`, cursor:'pointer', fontFamily:'inherit' }}><FiPaperclip size={12}/> View Document</button></div>)}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ padding:'10px 14px', background:'#FEF3DC', borderRadius:9, marginBottom:16, fontSize:12.5, color:'#92400E', fontWeight:600, border:'1px solid #F0D798', display:'flex', alignItems:'center', gap:8 }}>
                            <FiAlertCircle size={14}/> You are editing sanction details.
                          </div>
                          <form onSubmit={handleSanction}>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                              <Fl label="Sanctioned Amount (₹)" req><input type="number" placeholder="e.g. 5000000" min="1" value={sanctionForm.sanctioned_amount} onChange={e => setSanctionForm(f=>({...f,sanctioned_amount:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                              <Fl label="Sanction Date"><input type="date" max={todayStr()} value={sanctionForm.sanction_date} onChange={e => setSanctionForm(f=>({...f,sanction_date:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                              <Fl label="Expected Disbursement Date" req hint="Within 3 months of sanction date"><input type="date" min={sanctionForm.sanction_date||todayStr()} max={(() => { const d=new Date(sanctionForm.sanction_date||todayStr()); d.setDate(d.getDate()+90); return d.toISOString().split('T')[0]; })()} value={sanctionForm.expected_disbursement_date} onChange={e => setSanctionForm(f=>({...f,expected_disbursement_date:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
<Fl label="App No / LOS No" hint="Auto from Login"><input type="text" disabled value={sanctionForm.los_number} style={{ ...inputSt, height:40, background:'#F3F4F6', color:'#374151' }} /></Fl>
                              <div style={{ gridColumn:'span 2' }}><Fl label="Remarks" hint="Optional"><textarea rows={2} autoComplete="off" placeholder="Notes..." value={sanctionForm.remarks} onChange={e => setSanctionForm(f=>({...f,remarks:e.target.value}))} style={{ ...inputSt, height:'auto', paddingTop:10, paddingBottom:10, resize:'vertical', lineHeight:1.6 }} onFocus={iF} onBlur={iB}/></Fl></div>
                              <div style={{ gridColumn:'span 2' }}>
                                <Fl label="Approval Letter" hint="Replace existing document (optional)">
                                  <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                                    <input type="file" ref={sanctionFileRef} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => setSanctionFile(e.target.files[0]||null)} style={{ display:'none' }}/>
                                    <button type="button" onClick={() => sanctionFileRef.current?.click()} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:8, border:`1.5px solid ${sanctionFile?C.navy:C.border}`, background:sanctionFile?C.navyL:'#FAFBFC', color:sanctionFile?C.navy:C.t3, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                                      <FiPaperclip size={13}/> {sanctionFile ? 'Change File' : 'Replace Approval Letter'}
                                    </button>
                                    {sanctionFile && (<span style={{ fontSize:12.5, color:C.navy, fontWeight:600, display:'flex', alignItems:'center', gap:5 }}><FiCheckCircle size={12} style={{ color:C.green }}/> {sanctionFile.name}<button type="button" onClick={() => { setSanctionFile(null); if(sanctionFileRef.current) sanctionFileRef.current.value=''; }} style={{ background:'none', border:'none', cursor:'pointer', color:C.red, fontSize:11, padding:'0 4px' }}>✕</button></span>)}
                                    {!sanctionFile && sanctionFileUrl && (<button type="button" onClick={() => openDocument(sanctionFileUrl)} style={{ fontSize:12.5, color:C.sky, fontWeight:600, display:'inline-flex', alignItems:'center', gap:5, background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', padding:0 }}><FiPaperclip size={11}/> Existing letter</button>)}
                                  </div>
                                </Fl>
                              </div>
                            </div>
                            <div style={{ display:'flex', gap:10, marginTop:16 }}>
                              <SaveBtn saving={saving} label="Update Sanction" color={C.sky}/>
                              <button type="button" onClick={() => setSanctionEditMode(false)} disabled={saving} style={{ padding:'10px 18px', borderRadius:9, border:`1px solid ${C.border}`, background:C.white, color:C.t3, fontWeight:600, fontSize:13.5, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
                            </div>
                          </form>
                        </>
                      )}
                    </Card>
                  )}

                  {/* BR-02: Partial disbursement allowed even at PDD stage */}
                  {isConverted && (['SANCTION','DISBURSEMENT'].includes(currentStageRaw) || (currentStageRaw === 'PDD' && disbursements.some(d => ['PARTIAL','Partial'].includes(d.disbursement_type)))) && (
                    <Card title="Disbursement Tranches" icon={<span style={{fontSize:13,fontWeight:700}}>₹</span>} accent={C.green}>
                      {disbursements.length > 0 && (
                        <div style={{ marginBottom:20 }}>
                          <div style={{ fontSize:12, fontWeight:700, color:C.t2, marginBottom:10, textTransform:'uppercase', letterSpacing:'0.06em' }}>Recorded Tranches</div>
                          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                            <thead><tr style={{ background:'#F7F9FB' }}>{['#','Type','Date','Amount','Insurance',''].map(h => <th key={h} style={{ padding:'8px 12px', textAlign:h==='Amount'||h==='Insurance'?'right':'left', fontSize:10.5, fontWeight:700, color:C.t4, textTransform:'uppercase', letterSpacing:'0.08em', borderBottom:`1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
                            <tbody>
                              {disbursements.map((d, i) => (
                                <tr key={d.id} style={{ borderBottom:`1px solid ${C.borderL}` }}>
                                  <td style={{ padding:'9px 12px', color:C.t4, fontSize:12 }}>{i+1}</td>
                                  <td style={{ padding:'9px 12px' }}><span style={{ background:['Full','FULL'].includes(d.disbursement_type)?C.greenL:C.goldL, color:['Full','FULL'].includes(d.disbursement_type)?C.green:'#92670C', padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700 }}>{d.disbursement_type}</span></td>
                                  <td style={{ padding:'9px 12px', color:C.t2 }}>{fmtDate(d.disbursement_date)}</td>
                                  <td style={{ padding:'9px 12px', textAlign:'right', fontWeight:700, color:C.green }}>{fmtAmt(d.amount)}</td>
                                  <td style={{ padding:'9px 12px', textAlign:'right', color:C.t3, fontSize:12.5 }}>{d.insurance_amount?fmtAmt(d.insurance_amount):'—'}</td>
                                  <td style={{ padding:'9px 12px', textAlign:'right' }}><button onClick={() => handleDeleteTranche(d.id)} disabled={saving} style={{ width:28, height:28, borderRadius:7, border:`1px solid ${C.redB}`, background:'#fff', color:C.red, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center' }} onMouseEnter={e=>{e.currentTarget.style.background=C.redL;}} onMouseLeave={e=>{e.currentTarget.style.background='#fff';}}><FiTrash2 size={12}/></button></td>
                                </tr>
                              ))}
                              <tr style={{ background:'#F7F9FB' }}><td colSpan={3} style={{ padding:'9px 12px', fontWeight:700, color:C.t2 }}>Total</td><td style={{ padding:'9px 12px', textAlign:'right', fontWeight:800, color:C.navy, fontSize:14 }}>{fmtAmt(totalDisbursed)}</td><td colSpan={2}/></tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                      <div style={{ background:'#F8FAFC', borderRadius:10, padding:16, border:`1px solid ${C.border}` }}>
                        {(disbursements.some(d => ['FULL','Full'].includes(d.disbursement_type)) || (sanctionRecord?.sanctioned_amount && totalDisbursed >= Number(sanctionRecord.sanctioned_amount))) ? (
                          <div style={{ padding:'12px 16px', background:C.greenL, borderRadius:9, fontSize:13, color:'#166534', fontWeight:600, border:`1px solid ${C.greenB}`, display:'flex', alignItems:'center', gap:8 }}><FiCheckCircle size={14}/> {disbursements.some(d => ['FULL','Full'].includes(d.disbursement_type)) ? 'Full disbursement recorded' : `Total disbursed (${fmtAmt(totalDisbursed)}) equals sanctioned amount`} — no further tranches allowed.</div>
                        ) : (
                          <>
                            <div style={{ fontSize:12, fontWeight:700, color:C.t2, marginBottom:12, display:'flex', alignItems:'center', gap:6 }}><FiPlus size={13}/> {disbursements.length > 0 ? 'Add Partial Tranche' : 'Record Disbursement'}</div>
                            <form onSubmit={handleDisbursement}>
                              <Fl label="Disbursement Type" req>
                                <div style={{ display:'flex', gap:24, marginTop:8 }}>
                                  {(disbursements.some(d => ['PARTIAL','Partial'].includes(d.disbursement_type))
                                    ? [['PARTIAL','Partial Disbursement']] // already partial — only allow partial
                                    : [['FULL','Full Disbursement'],['PARTIAL','Partial Disbursement']]
                                  ).map(([val,label]) => (<label key={val} style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer', fontSize:13.5, fontWeight:600, color:C.t1 }}><input type="radio" name="dt" value={val} checked={disbForm.disbursement_type===val} onChange={() => setDisbForm(f=>({...f,disbursement_type:val}))} style={{ accentColor:C.navy }}/> {label}</label>))}
                                </div>
                              </Fl>
                              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginTop:16 }}>
                                <Fl label="Disbursement Date" req><input type="date" max={todayStr()} value={disbForm.disbursement_date} onChange={e => setDisbForm(f=>({...f,disbursement_date:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                                <Fl label={`Disbursed Amount (₹)${sanctionRecord?.sanctioned_amount ? ` — Remaining: ${fmtAmt(Number(sanctionRecord.sanctioned_amount) - totalDisbursed)}` : ""}`} req><input type="number" placeholder="₹" min="1" max={sanctionRecord?.sanctioned_amount ? Number(sanctionRecord.sanctioned_amount) - totalDisbursed : undefined} value={disbForm.amount} onChange={e => setDisbForm(f=>({...f,amount:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                                <Fl label="Insurance Amount (₹)" hint="Optional"><input type="number" placeholder="0" min="0" value={disbForm.insurance_amount} onChange={e => setDisbForm(f=>({...f,insurance_amount:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                              </div>
                              <div style={{ marginTop:16 }}><SaveBtn saving={saving} label="Record Disbursement" icon={<FiPlus size={13}/>}/></div>
                            </form>
                          </>
                        )}
                      </div>
                    </Card>
                  )}

                  {isConverted && currentStageRaw === 'DISBURSEMENT' && (
                    <Card title="Mark PDD — Post Disbursement Documents" icon={<FiCheckCircle size={13}/>} accent={C.purple}>

                      {/* ── Info banner — different for user vs admin ── */}
                      {!isAdmin() ? (
                        <div style={{ padding:'12px 14px', background:'#EDE9FE', borderRadius:9, marginBottom:16, fontSize:12.5, color:'#5B21B6', fontWeight:600, border:'1px solid #C4B5FD', display:'flex', alignItems:'center', gap:8 }}>
                          <FiInfo size={14} style={{ flexShrink:0 }}/>
                          Fill banker details and submit — <strong>Admin will approve PDD clearance.</strong>
                        </div>
                      ) : (
                        <div style={{ padding:'12px 14px', background:C.purpleL, borderRadius:9, marginBottom:16, fontSize:12.5, color:'#5B21B6', fontWeight:600, border:'1px solid #C4B5FD', display:'flex', alignItems:'center', gap:8 }}>
                          <FiShield size={14} style={{ flexShrink:0 }}/>
                          Admin mode — you can directly set PDD status to Cleared.
                        </div>
                      )}

                      <form onSubmit={handlePdd}>
                        <div style={{ fontSize:12, fontWeight:700, color:C.t2, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:12 }}>PDD Details</div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>

                          {/* Status — Admin sees full dropdown, User sees read-only "Pending" */}
                          {isAdmin() ? (
                            <Fl label="PDD Status" req>
                              <select value={pddForm.status} onChange={e => setPddForm(f=>({...f,status:e.target.value,is_cleared:e.target.value==='CLEARED'}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}>
                                <option value="PENDING">Pending</option>
                                <option value="CLEARED">Cleared</option>
                                <option value="REJECTED">Rejected</option>
                              </select>
                            </Fl>
                          ) : (
                            <Fl label="PDD Status">
                              <div style={{ height:40, display:'flex', alignItems:'center', padding:'0 12px', background:'#F9FAFB', border:`1px solid ${C.border}`, borderRadius:9, fontSize:13, color:C.t3, fontStyle:'italic' }}>
                                Pending Admin Approval
                              </div>
                            </Fl>
                          )}

                          <Fl label="Clearance Date" req>
                            <input type="date" max={todayStr()} value={pddForm.clearance_date} onChange={e => setPddForm(f=>({...f,clearance_date:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/>
                          </Fl>

                          {isAdmin() && pddForm.status === 'REJECTED' && (
                            <div style={{ gridColumn:'span 2' }}>
                              <Fl label="Rejection Reason" req>
                                <input type="text" placeholder="Reason for rejection" value={pddForm.rejection_reason} onChange={e => setPddForm(f=>({...f,rejection_reason:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/>
                              </Fl>
                            </div>
                          )}
                        </div>

                        <div style={{ height:1, background:C.borderL, margin:'20px 0' }}/>
                        <div style={{ fontSize:12, fontWeight:700, color:C.t2, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:12, display:'flex', alignItems:'center', gap:7 }}>
                          <FiUsers size={13}/> Banker Details (Required)
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
                          <Fl label="Banker Name" req><div style={{ position:'relative' }}><FiUser size={13} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/><input type="text" placeholder="Full name" value={pddForm.banker_name} onChange={e => setPddForm(f=>({...f,banker_name:e.target.value}))} style={{ ...inputSt, height:40, paddingLeft:34 }} onFocus={iF} onBlur={iB}/></div></Fl>
                          <Fl label="Contact Number" req><div style={{ position:'relative' }}><FiPhone size={13} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/><input type="text" placeholder="Mobile" value={pddForm.banker_contact} onChange={e => setPddForm(f=>({...f,banker_contact:e.target.value}))} maxLength={15} style={{ ...inputSt, height:40, paddingLeft:34 }} onFocus={iF} onBlur={iB}/></div></Fl>
                          <Fl label="Email" hint="Optional"><div style={{ position:'relative' }}><FiMail size={13} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/><input type="email" placeholder="banker@bank.com" value={pddForm.banker_email} onChange={e => setPddForm(f=>({...f,banker_email:e.target.value}))} style={{ ...inputSt, height:40, paddingLeft:34 }} onFocus={iF} onBlur={iB}/></div></Fl>
                        </div>
                        <div style={{ marginTop:20 }}>
                          <SaveBtn saving={saving}
                            label={isAdmin() ? 'Save PDD & Banker Details' : 'Submit PDD for Admin Approval'}
                            color={C.purple}/>
                        </div>
                      </form>
                    </Card>
                  )}

                  {/* ── BANNER: PDD submitted by user, awaiting Admin approval ── */}
                  {isConverted && ['PDD','PDD_PENDING','REVENUE'].includes(currentStageRaw) && pddRecord && pddRecord.status === 'PENDING' && !isAdmin() && (
                    <div style={{ padding:'16px 18px', background:'linear-gradient(135deg,#FEF3DC,#FFF7ED)', borderRadius:12, border:'1.5px solid #FDE68A', display:'flex', alignItems:'flex-start', gap:12 }}>
                      <div style={{ width:38, height:38, borderRadius:10, background:'#F5A623', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <FiClock size={18} style={{ color:'#fff' }}/>
                      </div>
                      <div>
                        <div style={{ fontSize:14, fontWeight:800, color:'#92400E', marginBottom:4 }}>
                          PDD submitted — awaiting Admin approval
                        </div>
                        <div style={{ fontSize:12.5, color:'#B45309', lineHeight:1.5 }}>
                          Your PDD details have been saved. The Admin will review and approve or reject this PDD.
                          Revenue stage will unlock automatically once Admin clears it.
                        </div>
                        <div style={{ marginTop:8, fontSize:11.5, color:'#D97706', display:'flex', alignItems:'center', gap:5 }}>
                          <FiCalendar size={11}/>
                          Submitted: {fmtDate(pddRecord.clearance_date)} · Banker: {pddRecord.banker_name || bankerRecord?.banker_name || '—'}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── ADMIN UPDATE FORM — only Admin sees this when PDD is PENDING/REJECTED ── */}
                  {isConverted && ['PDD','PDD_PENDING','REVENUE'].includes(currentStageRaw) && pddRecord && pddRecord.status !== 'CLEARED' && isAdmin() && (
                    <Card title="Update PDD Status" icon={<FiCheckCircle size={13}/>} accent={C.purple}>
                      <div style={{ padding:'10px 14px', background:'#FEF3DC', borderRadius:9, marginBottom:16, fontSize:12.5, color:'#92400E', fontWeight:600, border:'1px solid #F0D798', display:'flex', alignItems:'center', gap:8 }}>
                        <FiAlertCircle size={14}/> PDD status: <strong>{pddRecord.status}</strong> — Revenue unlocks only after PDD is CLEARED.
                      </div>
                      <form onSubmit={handlePdd}>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
                          <Fl label="PDD Status" req>
                            <select value={pddForm.status} onChange={e => setPddForm(f=>({...f,status:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}>
                              <option value="PENDING">Pending</option>
                              <option value="CLEARED">Cleared</option>
                              <option value="REJECTED">Rejected</option>
                            </select>
                          </Fl>
                          <Fl label="Clearance Date" req>
                            <input type="date" max={todayStr()} value={pddForm.clearance_date} onChange={e => setPddForm(f=>({...f,clearance_date:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/>
                          </Fl>
                          {pddForm.status === 'REJECTED' && (
                            <div style={{ gridColumn:'span 2' }}>
                              <Fl label="Rejection Reason" req>
                                <input type="text" value={pddForm.rejection_reason} onChange={e => setPddForm(f=>({...f,rejection_reason:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/>
                              </Fl>
                            </div>
                          )}
                        </div>
                        <div style={{ height:1, background:C.borderL, marginBottom:16 }}/>
                        <div style={{ fontSize:12, fontWeight:700, color:C.t2, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:12, display:'flex', alignItems:'center', gap:7 }}>
                          <FiUsers size={13}/> Banker Details
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
                          <Fl label="Banker Name" req><div style={{ position:'relative' }}><FiUser size={13} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/><input type="text" placeholder="Full name" value={pddForm.banker_name} onChange={e => setPddForm(f=>({...f,banker_name:e.target.value}))} style={{ ...inputSt, height:40, paddingLeft:34 }} onFocus={iF} onBlur={iB}/></div></Fl>
                          <Fl label="Contact Number" req><div style={{ position:'relative' }}><FiPhone size={13} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/><input type="text" placeholder="Mobile" value={pddForm.banker_contact} onChange={e => setPddForm(f=>({...f,banker_contact:e.target.value}))} maxLength={15} style={{ ...inputSt, height:40, paddingLeft:34 }} onFocus={iF} onBlur={iB}/></div></Fl>
                          <Fl label="Email" hint="Optional"><div style={{ position:'relative' }}><FiMail size={13} style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', color:C.t4, pointerEvents:'none' }}/><input type="email" placeholder="banker@bank.com" value={pddForm.banker_email} onChange={e => setPddForm(f=>({...f,banker_email:e.target.value}))} style={{ ...inputSt, height:40, paddingLeft:34 }} onFocus={iF} onBlur={iB}/></div></Fl>
                        </div>
                        <div style={{ marginTop:16 }}><SaveBtn saving={saving} label="Update PDD Status" color={C.purple}/></div>
                      </form>
                    </Card>
                  )}

                  {isConverted && ['PDD','PDD_PENDING','REVENUE'].includes(currentStageRaw) && pddRecord && pddRecord.status === 'CLEARED' && (
                    <Card title="PDD & Banker Details" icon={<FiCheckCircle size={13}/>} accent={C.purple}>
                      <div style={{ padding:'10px 14px', background:C.purpleL, borderRadius:9, marginBottom:16, fontSize:12.5, color:'#5B21B6', fontWeight:600, border:'1px solid #C4B5FD', display:'flex', alignItems:'center', gap:8 }}><FiCheckCircle size={14}/> PDD Cleared — stage is now locked.</div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
                        <LV label="PDD Status" value={pddRecord.status} bold highlight={C.green}/>
                        <LV label="Clearance Date" value={fmtDate(pddRecord.clearance_date)}/>
                        {pddRecord.rejection_reason && <LV label="Rejection Reason" value={pddRecord.rejection_reason}/>}
                        {bankerRecord && (<><LV label="Banker Name" value={bankerRecord.banker_name} bold/><LV label="Banker Contact" value={bankerRecord.banker_contact} mono/>{bankerRecord.banker_email && <LV label="Banker Email" value={bankerRecord.banker_email}/>}</>)}
                      </div>
                    </Card>
                  )}

                  {/* ✅ FIX: Record Revenue shown only when PDD is CLEARED */}
                  {isConverted && currentStageRaw === 'REVENUE' && !isTerminated && isPddCleared && (
                    <Card title="Revenue Workflow" icon={<span style={{fontSize:13,fontWeight:700}}>₹</span>} accent={C.teal}>
                      <div style={{ padding:'10px 14px', background:C.tealL, borderRadius:9, marginBottom:16, fontSize:12.5, color:'#0F766E', fontWeight:600, border:`1px solid ${C.tealB}` }}>
                        Revenue and invoice entry are managed from the Admin workflow pages. This lead page shows the latest status and quick navigation.
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:16 }}>
                        <LV label="Invoice Required" value={revenueRecord ? (revenueRecord.invoice_required === false ? 'No' : 'Yes') : 'Awaiting PDD approval'} />
                        {revenueRecord?.invoice_number && <LV label="Invoice Number" value={revenueRecord.invoice_number} mono bold/>}
                        {revenueRecord?.invoice_date && <LV label="Invoice Date" value={fmtDate(revenueRecord.invoice_date)}/>}
                        {revenueRecord?.payout_to_company && <LV label="Payout to Company" value={fmtAmt(revenueRecord.payout_to_company)} bold highlight={C.teal}/>}
                        {revenueRecord?.received_amount && <LV label="Received Amount" value={fmtAmt(revenueRecord.received_amount)} highlight={C.green}/>}
                        {revenueRecord?.date_of_receipt && <LV label="Date of Receipt" value={fmtDate(revenueRecord.date_of_receipt)}/>}
                        {revenueRecord?.mode_of_receipt && <LV label="Mode of Receipt" value={revenueRecord.mode_of_receipt}/>}
                      </div>
                      <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                        {revenueRecord?.invoice_required !== false && (
                          <button type="button" onClick={() => navigate('/invoice-management')} className="btn-navy" style={{ justifyContent:'center', fontSize:13 }}>
                            <FiFileText size={13}/> Go to Invoice Management
                          </button>
                        )}
                        <button type="button" onClick={() => navigate('/revenue-management')} className="btn-navy" style={{ justifyContent:'center', fontSize:13, background:C.teal }}>
                          <FiTrendingUp size={13}/> Go to Revenue Management
                        </button>
                      </div>
                    </Card>
                  )}

                  {false && isConverted && ['PDD','PDD_PENDING','REVENUE'].includes(currentStageRaw) && !isTerminated && isPddCleared && !(currentStageRaw === 'REVENUE' && isPddFrozen) && (
                    <Card title="Record Revenue" icon={<span style={{fontSize:13,fontWeight:700}}>₹</span>} accent={C.teal}>
                      <div style={{ padding:'10px 14px', background:C.tealL, borderRadius:9, marginBottom:16, fontSize:12.5, color:'#0F766E', fontWeight:600, border:`1px solid ${C.tealB}` }}>
                        Record here the payout the company receives from the lender after PDD.
                      </div>
                      <form onSubmit={handleRevenue}>
                        <div style={{ marginBottom:20, padding:'14px 16px', background:'#F8FAFC', borderRadius:10, border:`1px solid ${C.border}` }}>
                          <div style={{ fontSize:12, fontWeight:700, color:C.t2, marginBottom:10, textTransform:'uppercase', letterSpacing:'0.06em' }}>Invoice Required?</div>
                          <div style={{ display:'flex', gap:10 }}>
                            {[{ val:true, label:'Yes — Invoice Required', desc:'Scenario 1: Enter invoice number & date' }, { val:false, label:'No — Skip Invoice', desc:'Scenario 2: Go directly to revenue' }].map(opt => (
                              <label key={String(opt.val)} onClick={() => setRevenueForm(f=>({...f,invoice_required:opt.val}))} style={{ flex:1, display:'flex', alignItems:'flex-start', gap:10, padding:'12px 14px', borderRadius:9, border:`1.5px solid ${revenueForm.invoice_required===opt.val ? C.teal : C.border}`, background: revenueForm.invoice_required===opt.val ? C.tealL : C.white, cursor:'pointer', transition:'all 0.15s' }}>
                                <div style={{ width:16, height:16, borderRadius:'50%', border:`2px solid ${revenueForm.invoice_required===opt.val ? C.teal : C.border}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:2, background: revenueForm.invoice_required===opt.val ? C.teal : 'transparent' }}>
                                  {revenueForm.invoice_required===opt.val && <div style={{ width:6, height:6, borderRadius:'50%', background:'#fff' }}/>}
                                </div>
                                <div><div style={{ fontSize:13, fontWeight:700, color: revenueForm.invoice_required===opt.val ? '#0F766E' : C.t1 }}>{opt.label}</div><div style={{ fontSize:11.5, color:C.t4, marginTop:2 }}>{opt.desc}</div></div>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                          {revenueForm.invoice_required && (<><Fl label="Invoice Number" req><input type="text" placeholder="INV-2025-001" value={revenueForm.invoice_number} onChange={e => setRevenueForm(f=>({...f,invoice_number:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl><Fl label="Invoice Date" req><input type="date" max={todayStr()} value={revenueForm.invoice_date} onChange={e => setRevenueForm(f=>({...f,invoice_date:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl></>)}
                          {!revenueForm.invoice_required && (<div style={{ gridColumn:'span 2', display:'flex', alignItems:'center', gap:9, padding:'12px 14px', background:'#FFFBEB', borderRadius:9, border:'1px solid #FDE68A', fontSize:12.5, color:'#92400E', fontWeight:600 }}><FiInfo size={14} style={{ flexShrink:0 }}/>Invoice is being skipped. Payout to Company and Revenue Received will be recorded directly.</div>)}
                          <Fl label="Payout to Company (₹)" req hint="Commission received from lender"><input type="number" placeholder="₹" min="0" step="0.01" value={revenueForm.payout_to_company} onChange={e => setRevenueForm(f=>({...f,payout_to_company:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                          <Fl label="Revenue Received (₹)" req><input type="number" placeholder="₹" min="0" step="0.01" value={revenueForm.revenue_received} onChange={e => setRevenueForm(f=>({...f,revenue_received:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                          <Fl label="Received Amount (₹)" hint="What was actually received"><input type="number" placeholder="₹" min="0" step="0.01" value={revenueForm.received_amount} onChange={e => setRevenueForm(f=>({...f,received_amount:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                          <Fl label="Date of Receipt"><input type="date" max={todayStr()} value={revenueForm.date_of_receipt} onChange={e => setRevenueForm(f=>({...f,date_of_receipt:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                          <Fl label="Mode of Receipt">
                            <select value={revenueForm.mode_of_receipt} onChange={e => setRevenueForm(f=>({...f,mode_of_receipt:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}>
                              <option value="">— Select —</option>
                              {['NEFT','RTGS','CHEQUE','CASH','UPI'].map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                          </Fl>
                        </div>
                        <div style={{ marginTop:20 }}>
                          <SaveBtn saving={saving} label={revenueRecord ? 'Update Revenue' : 'Save Revenue'} color={C.teal}/>
                        </div>
                      </form>
                    </Card>
                  )}

                  {isConverted && currentStageRaw === 'REVENUE' && revenueRecord && (
                    <Card title="Revenue Details" icon={<span style={{fontSize:13,fontWeight:700}}>₹</span>} accent={C.teal}>
                      <div style={{ padding:'10px 14px', background:C.tealL, borderRadius:9, marginBottom:16, fontSize:12.5, color:'#0F766E', fontWeight:600, border:`1px solid ${C.tealB}`, display:'flex', alignItems:'center', gap:8 }}><FiCheckCircle size={14}/> Revenue recorded — lead has entered its final stage.</div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
                        {revenueRecord.invoice_required !== false && revenueRecord.invoice_number && <LV label="Invoice Number" value={revenueRecord.invoice_number} mono bold/>}
                        {revenueRecord.invoice_required !== false && revenueRecord.invoice_date   && <LV label="Invoice Date" value={fmtDate(revenueRecord.invoice_date)}/>}
                        {revenueRecord.invoice_required === false && <LV label="Invoice" value="Not Required"/>}
                        <LV label="Payout to Company" value={fmtAmt(revenueRecord.payout_to_company)} bold highlight={C.teal}/>
                        {revenueRecord.revenue_received && <LV label="Revenue Received" value={fmtAmt(revenueRecord.revenue_received)} highlight={C.green}/>}
                        {revenueRecord.received_amount && <LV label="Received Amount" value={fmtAmt(revenueRecord.received_amount)} highlight={C.green}/>}
                        {revenueRecord.date_of_receipt && <LV label="Date of Receipt" value={fmtDate(revenueRecord.date_of_receipt)}/>}
                        {revenueRecord.mode_of_receipt && <LV label="Mode of Receipt" value={revenueRecord.mode_of_receipt}/>}
                      </div>
                    </Card>
                  )}

                  {/* Mark as Complete — only at REVENUE stage */}
                  {!isTerminated && currentStageRaw === 'REVENUE' && (
                    <Card title="Complete Lead" icon={<FiCheckCircle size={13}/>} accent={C.green}>
                      <div style={{ padding:'10px 14px', background:C.greenL, borderRadius:9, marginBottom:16, fontSize:13, color:'#166534', border:`1px solid ${C.greenB}` }}>Revenue has been collected. Mark this lead as Completed.</div>
                      <button onClick={handleComplete} disabled={saving} style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'10px 22px', borderRadius:9, background:saving?C.t4:C.green, color:'#fff', border:'none', fontWeight:700, fontSize:13.5, cursor:saving?'not-allowed':'pointer', fontFamily:'inherit', boxShadow:`0 2px 8px ${C.green}40`, transition:'all 0.12s' }}>
                        {saving ? <><Spinner/> Saving…</> : <><FiCheckCircle size={13}/> Mark as Completed</>}
                      </button>
                    </Card>
                  )}

                  {/* FR-7: Lead can be closed at FOLLOW_UP, LOGIN, SANCTION stages */}
                  {!isTerminated && ['LOGIN','SANCTION','FOLLOW_UP',''].includes(currentStageRaw) && (
                    <Card title="Close Lead" icon={<FiXCircle size={13}/>} accent={C.red}>
                      <div style={{ padding:'10px 14px', background:C.redL, borderRadius:9, marginBottom:16, fontSize:13, color:C.red, border:`1px solid ${C.redB}` }}>Close this lead? (Lost to Competition / Deal Called Off / Eligibility Issue etc.)</div>
                      <form onSubmit={handleClose}>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                          <Fl label="Closure Reason" req>
                            <select value={closeForm.closure_reason} onChange={e => setCloseForm(f=>({...f,closure_reason:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}>
                              <option value="">— Select Reason —</option>
                              {(CLOSURE_REASONS||[]).map(r => <option key={r.name||r} value={r.name||r}>{r.name||r}</option>)}
                            </select>
                          </Fl>
                          <Fl label="Remarks" hint="Optional"><input type="text" placeholder="Any additional details..." value={closeForm.closure_remarks} onChange={e => setCloseForm(f=>({...f,closure_remarks:e.target.value}))} style={{ ...inputSt, height:40 }} onFocus={iF} onBlur={iB}/></Fl>
                        </div>
                        <div style={{ marginTop:16 }}>
                          <button type="submit" disabled={saving} style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'10px 22px', borderRadius:9, background:saving?C.t4:C.red, color:'#fff', border:'none', fontWeight:700, fontSize:13.5, cursor:saving?'not-allowed':'pointer', fontFamily:'inherit', boxShadow:`0 2px 8px ${C.red}40`, transition:'all 0.12s' }}>
                            {saving ? <><Spinner/> Closing…</> : <><FiXCircle size={13}/> Close Lead</>}
                          </button>
                        </div>
                      </form>
                    </Card>
                  )}
                </>
              )}

              {isTerminated && (
                <div style={{ textAlign:'center', padding:52, background:isCompleted?C.greenL:C.redL, border:`1.5px solid ${isCompleted?C.greenB:C.redB}`, borderRadius:14 }}>
                  <div style={{ width:64, height:64, borderRadius:'50%', background:isCompleted?C.green:C.red, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', boxShadow:`0 4px 16px ${isCompleted?C.green:C.red}40` }}>{isCompleted ? <FiCheckCircle size={28} style={{ color:'#fff' }}/> : <FiXCircle size={28} style={{ color:'#fff' }}/>}</div>
                  <h3 style={{ color:isCompleted?'#166534':'#991B1B', margin:'0 0 7px', fontSize:18, fontWeight:800 }}>{isCompleted ? 'Lead Completed' : 'Lead Closed'}</h3>
                  <p style={{ color:isCompleted?'#166534':C.red, margin:0, fontSize:13.5 }}>{isCompleted ? 'Final successful state.' : `Cannot be modified.${lead.closure_reason ? ` Reason: ${lead.closure_reason}` : ''}`}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <Card title="Activity Timeline" icon={<FiActivity size={13}/>} noPad style={{ animation:'cfSlideDown 0.18s ease both' }}>
              <div style={{ position:'relative', padding:'20px 24px 20px 52px' }}>
                <div style={{ position:'absolute', left:32, top:20, bottom:20, width:2, background:C.borderL, borderRadius:2 }}/>
                {[
                  { time:lead.lead_date||lead.created_at, user:lead.created_by_detail, action:'Lead created', detail:`Source: ${lead.lead_source} | Product: ${lead.product?.name||'—'}`, dotBg:C.navyL, dotBorder:C.navy },
                  loginRecord ? { time:loginRecord.created_at||loginRecord.login_date, user:null, action:`Login recorded — ${loginRecord.login_type}`, detail:`${loginRecord.institution_detail?.name||loginRecord.channel_detail?.name||''}${loginRecord.application_number?` | App: ${loginRecord.application_number}`:''}`, dotBg:C.skyL, dotBorder:C.sky } : null,
                  sanctionRecord ? { time:sanctionRecord.created_at||sanctionRecord.sanction_date, user:null, action:'Sanction recorded', detail:`Amount: ${fmtAmt(sanctionRecord.sanctioned_amount)} | Expected Disb: ${fmtDate(sanctionRecord.expected_disbursement_date)}`, dotBg:C.greenL, dotBorder:C.green } : null,
                  ...disbursements.map(d => ({ time:d.created_at||d.disbursement_date, user:null, action:`Disbursement — ${d.disbursement_type}`, detail:`${fmtAmt(d.amount)} on ${fmtDate(d.disbursement_date)}${d.insurance_amount?` | Insurance: ${fmtAmt(d.insurance_amount)}`:''}`, dotBg:C.greenL, dotBorder:C.green })),
                  pddRecord ? { time:pddRecord.created_at||pddRecord.clearance_date, user:null, action:`PDD — ${pddRecord.status}`, detail:`Clearance: ${fmtDate(pddRecord.clearance_date)}${bankerRecord?` | Banker: ${bankerRecord.banker_name}`:''}`, dotBg:C.purpleL, dotBorder:C.purple } : null,
                  revenueRecord ? { time:revenueRecord.created_at||revenueRecord.invoice_date, user:null, action:'Revenue recorded', detail:`${revenueRecord.invoice_required===false ? 'No invoice' : `Invoice: ${revenueRecord.invoice_number||'—'}`} | Payout: ${fmtAmt(revenueRecord.payout_to_company)}`, dotBg:C.tealL, dotBorder:C.teal } : null,
                  lead.next_follow_up_date ? { time:lead.next_follow_up_date, user:null, action:'Follow-up scheduled', detail:fmtDate(lead.next_follow_up_date), dotBg:C.goldL, dotBorder:C.gold } : null,
                  isTerminated && (isCompleted || lead.closure_reason) ? { 
                    time:lead.updated_at, 
                    user:lead.updated_by_detail, 
                    action: isCompleted ? 'Lead completed' : 'Lead closed', 
                    detail: isCompleted ? 'Lead finished successfully' : `Reason: ${lead.closure_reason}${lead.closure_remarks?` | ${lead.closure_remarks}`:''}`, 
                    dotBg: isCompleted ? C.greenL : C.redL, 
                    dotBorder: isCompleted ? C.green : C.red 
                  } : null,
                ].filter(Boolean).sort((a,b) => new Date(b.time)-new Date(a.time)).map((ev, i) => (
                  <div key={i} style={{ position:'relative', marginBottom:16 }}>
                    <div style={{ position:'absolute', left:-28, top:6, width:14, height:14, borderRadius:'50%', border:`2px solid ${ev.dotBorder}`, background:ev.dotBg, boxShadow:'0 0 0 3px white' }}/>
                    <div style={{ background:'#F8FAFC', border:`1px solid ${C.border}`, borderRadius:11, padding:'13px 16px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:13.5, color:C.t1 }}>{ev.action}</div>
                          {ev.detail && <div style={{ fontSize:13, color:C.t3, marginTop:3 }}>{ev.detail}</div>}
                          {ev.user   && <div style={{ fontSize:12, color:C.t4, marginTop:5 }}>by {`${ev.user.first_name||''} ${ev.user.last_name||''}`.trim()||ev.user.email}</div>}
                        </div>
                        <div style={{ fontSize:12, color:C.t4, whiteSpace:'nowrap', flexShrink:0 }}>{fmtDateTime(ev.time)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

        </div>
      </Layout>
    </>
  );
}
